import React, { useState, useRef, useCallback, useMemo, useEffect, lazy, Suspense, useLayoutEffect } from 'react';
import { useNavigate, Routes, Route } from 'react-router-dom';
import useSWR from 'swr';
import debounce from 'lodash/debounce';
import HistoryIcon from '@mui/icons-material/History';
import SaveIcon from '@mui/icons-material/Save';
import CheckIcon from '@mui/icons-material/Check';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CodeIcon from '@mui/icons-material/Code';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ChartDisplay from './ChartDisplay';
import { 
  Box, 
  TextField, 
  Button, 
  Typography, 
  Paper, 
  CircularProgress, 
  IconButton, 
  Avatar, 
  Snackbar, 
  Tooltip, 
  Fade, 
  Alert, 
  Container, 
  useTheme,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  InputAdornment,
  AppBar,
  Toolbar,
  Menu,
  MenuItem,
  Badge,
  Modal, // Add Modal import
  useMediaQuery,
  Chip
} from '@mui/material';
import { useThemeMode } from '../contexts/ThemeContext';
import { alpha } from '@mui/material/styles'; // Import alpha for transparent colors
import SendIcon from '@mui/icons-material/Send';
import MicIcon from '@mui/icons-material/Mic';
import DownloadIcon from '@mui/icons-material/Download'; // Add DownloadIcon import
import ZoomInIcon from '@mui/icons-material/ZoomIn'; // Add ZoomInIcon import
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import ChatIcon from '@mui/icons-material/Chat';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import GroupsIcon from '@mui/icons-material/Groups';
import PersonIcon from '@mui/icons-material/Person';
import FolderIcon from '@mui/icons-material/Folder';
import LinkIcon from '@mui/icons-material/Link';
import SettingsIcon from '@mui/icons-material/Settings';
import SearchIcon from '@mui/icons-material/Search';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import HomeIcon from '@mui/icons-material/Home';
import MenuIcon from '@mui/icons-material/Menu'; // Import MenuIcon
import { styled } from '@mui/system';
import AgentMemory from './AgentMemory';
import {
  supabase,
  consumeCredits,
  ensureChatTablesExist,
  fetchChatSessions, // Import chat functions
  saveChatSession,
  updateChatSession,
  saveChatMessages,
  fetchChatMessages,
  saveFileForMessage,
  saveImagesForMessage
} from '../supabaseClient';
import SaveChatDialog from './SaveChatDialog';
import ChatHistory from './ChatHistory';
// import { runComfyDeployWorkflow } from '../api/comfyDeployApi'; // Removed ComfyUI
import { GoogleGenerativeAI } from "@google/generative-ai";
import { useInterval } from '../hooks/useInterval';
import { canvasWorkflows } from '../workflows/canvasWorkflows';
import { getSubscriptionDetails } from '../services/subscriptionService';
import { lightTheme, darkTheme } from '../theme';
import { ThemeProvider } from '@mui/material/styles';
import Gallery from './Gallery';
import PropertyGallery from './PropertyGallery';
import ReactMarkdown from 'react-markdown'; // Import ReactMarkdown
import CreditAlert from './CreditAlert'; // Import CreditAlert
import convert from 'heic-convert/browser'; // Import heic-convert for browser

// --- CSS for Animated Ellipsis and Fade-in ---
const AnimatedEllipsisStyle = `
  @keyframes ellipsis {
    0% { content: '.'; }
    33% { content: '..'; }
    66% { content: '...'; }
  }
  .thinking-ellipsis::after {
    display: inline-block;
    animation: ellipsis 1.5s infinite;
    content: '.'; /* Initial content */
    width: 1.5em; /* Allocate space for three dots */
    text-align: left;
    margin-left: 2px;
  }
  
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;
// --- End CSS ---

// Simple component for the animated ellipsis
const AnimatedEllipsis = () => <span className="thinking-ellipsis"></span>;

// Message options menu component
const MessageOptionsMenu = ({ message }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [copySuccess, setCopySuccess] = useState(false);
  
  const handleClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };
  
  const handleClose = () => {
    setAnchorEl(null);
  };
  
  const copyToClipboard = (text, isMarkdown = false) => {
    if (!text) return;
    
    let textToCopy = text;
    
    // If copying as markdown, don't add quote formatting
    // This allows for clean markdown export without ">" symbols
    if (isMarkdown) {
      // Just use the text as is for markdown export
      textToCopy = text;
      
      // If there's chart data, add a markdown representation of it
      if (message.chartData) {
        const chartData = message.chartData;
        
        // Add a header for the chart data
        textToCopy += '\n\n### Chart Data\n\n';
        
        // Add chart type
        textToCopy += `**Type:** ${chartData.type}\n\n`;
        
        // Add chart title if available
        if (chartData.options?.title?.text) {
          textToCopy += `**Title:** ${chartData.options.title.text}\n\n`;
        }
        
        // Add labels
        if (chartData.data?.labels?.length) {
          textToCopy += '**Labels:** ' + chartData.data.labels.join(', ') + '\n\n';
        }
        
        // Add datasets in a table format
        if (chartData.data?.datasets?.length) {
          textToCopy += '**Datasets:**\n\n';
          
          // For each dataset, create a markdown table
          chartData.data.datasets.forEach(dataset => {
            if (dataset.label) {
              textToCopy += `*${dataset.label}*\n\n`;
            }
            
            // Create table header
            textToCopy += '| Label | Value |\n| --- | --- |\n';
            
            // Add data rows
            chartData.data.labels.forEach((label, index) => {
              const value = dataset.data[index];
              textToCopy += `| ${label} | ${value} |\n`;
            });
            
            textToCopy += '\n';
          });
        }
      }
    }
    
    navigator.clipboard.writeText(textToCopy)
      .then(() => {
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      })
      .catch(err => {
        console.error('Failed to copy text: ', err);
      });
    
    handleClose();
  };
  
  return (
    <>
      <IconButton
        aria-label="message options"
        aria-controls={open ? "message-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={handleClick}
        size="small"
        sx={{ 
          opacity: 0.6, 
          '&:hover': { opacity: 1 },
          backgroundColor: 'transparent',
          padding: '2px'
        }}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      
      <Menu
        id="message-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'message-options-button',
        }}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={() => copyToClipboard(message.text)}>
          <ListItemIcon>
            <ContentCopyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Metni Kopyala</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => copyToClipboard(message.text, true)}>
          <ListItemIcon>
            <CodeIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Markdown Olarak Kopyala</ListItemText>
        </MenuItem>
      </Menu>
      
      {/* Success Snackbar */}
      <Snackbar
        open={copySuccess}
        autoHideDuration={2000}
        onClose={() => setCopySuccess(false)}
        message="Metin panoya kopyalandı"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  );
};

// Drawer width
const drawerWidth = '4.375rem'; // 70px converted to rem

// Styled components
const MessageBubble = styled(Box)(({ theme, sender }) => ({
  background: sender === 'user' 
    ? theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[100]
    : theme.palette.mode === 'dark' ? theme.palette.background.paper : theme.palette.background.paper,
  color: theme.palette.text.primary,
  padding: theme.spacing(1.5, 2),
  paddingRight: theme.spacing(3), // Add extra padding for the menu button
  maxWidth: '70%',
  borderRadius: '0.75rem', // 12px to rem
  boxShadow: theme.shadows[1],
  marginBottom: theme.spacing(1),
  position: 'relative',
  '&::after': sender === 'user' ? {
    content: '""',
    position: 'absolute',
    bottom: 0,
    right: '-0.5rem', // -8px to rem
    width: '0.9375rem', // 15px to rem
    height: '0.9375rem', // 15px to rem
    background: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[100],
    borderBottomLeftRadius: '50%',
    transform: 'translateY(-50%)'
  } : undefined,
  '&::before': sender !== 'user' ? {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: '-0.5rem', // -8px to rem
    width: '0.9375rem', // 15px to rem
    height: '0.9375rem', // 15px to rem
    background: theme.palette.background.paper,
    borderBottomRightRadius: '50%',
    transform: 'translateY(-50%)'
  } : undefined,
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '1.5625rem', // 25px to rem
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[1],
    '& fieldset': {
      border: 'none',
    },
    '&:hover fieldset': {
      border: 'none',
    },
    '&.Mui-focused fieldset': {
      border: 'none',
    },
  },
  '& .MuiOutlinedInput-input': {
    padding: '0.875rem 0.875rem 0.875rem 1.25rem', // 14px 14px 14px 20px to rem
    color: theme.palette.text.primary,
  },
  '& .MuiInputAdornment-root': {
    color: theme.palette.text.secondary,
  },
}));

const SidebarButton = styled(IconButton)(({ theme, active }) => ({
  color: active ? theme.palette.primary.main : theme.palette.text.secondary,
  backgroundColor: active ? theme.palette.action.selected : 'transparent',
  borderRadius: '0.75rem', // 12px to rem
  padding: theme.spacing(1.5),
  margin: theme.spacing(0.5, 0),
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  transition: 'background-color 0.2s, color 0.2s',
})); // <-- Added missing closing parenthesis and semicolon

// Function to convert base64 to blob for upload
const base64ToBlob = (base64, mimeType) => {
  const byteCharacters = atob(base64.split(',')[1]);
  const byteArrays = [];

  for (let i = 0; i < byteCharacters.length; i += 512) {
    const slice = byteCharacters.slice(i, i + 512);
    const byteNumbers = new Array(slice.length);
    
    for (let j = 0; j < slice.length; j++) {
      byteNumbers[j] = slice.charCodeAt(j);
    }
    
    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }

  return new Blob(byteArrays, { type: mimeType });
};

// Helper function to upload images to storage
const uploadImage = async (file, session) => {
  if (!file || !session || !session.user) {
    console.error('File or session is missing:', { file, session });
    return null;
  }
  const fileExt = file.name.split('.').pop();
  const fileName = `${Math.random()}.${fileExt}`;
  const filePath = `${session.user.id}/${fileName}`;

  try {
    const { data, error } = await supabase.storage
      .from('generated-images')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false
      });

    if (error) throw error;

    const { data: { publicUrl }, error: urlError } = supabase.storage
      .from('generated-images')
      .getPublicUrl(filePath);

    if (urlError) throw urlError;

    return publicUrl;
  } catch (error) {
    console.error('Error uploading image:', error);
    alert(`Error uploading image: ${error.message}`);
    return null;
  }
}; // <-- Added missing closing brace for uploadImage function

// Helper function to check supported MIME types for Gemini inlineData
const isMimeTypeSupportedByGemini = (mimeType) => {
  if (!mimeType) return false;
  // Common image types supported by Gemini
  if (mimeType.startsWith('image/png') ||
      mimeType.startsWith('image/jpeg') ||
      mimeType.startsWith('image/webp') ||
      mimeType.startsWith('image/heic') ||
      mimeType.startsWith('image/heif')) {
    return true;
  }
  // Add other supported types if needed (e.g., video, audio, pdf)
  // if (mimeType.startsWith('application/pdf')) return true;
  // if (mimeType.startsWith('video/')) return true;
  // if (mimeType.startsWith('audio/')) return true;
  return false;
};

// Define agent types and models (can be shared or defined here)
const AGENT_TYPE_TEXT = 'text';
const AGENT_TYPE_IMAGE = 'image'; // Default if not specified
const TEXT_MODEL = 'gemini-2.0-flash'; // Updated model name to fix quota issue
const IMAGE_MODEL = 'gemini-2.0-flash-exp-image-generation'; // Existing image model


// Create a hook to handle generation (renamed from useImageGeneration)
const useGenerationService = (session) => { // Pass session here
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState('Generating...'); // More generic initial message
  // const [generationMethod, setGenerationMethod] = useState('gemini'); // Only Gemini is used now

  // Generate image using Gemini API (existing function)
  const generateImage = async (params) => {
    setIsLoading(true);
    setProgress(10);
    setLoadingMessage('Preparing to generate image with Gemini...');

    try {
      // Initialize the Gemini API
      const genAI = new GoogleGenerativeAI(process.env.REACT_APP_GEMINI_API_KEY || "YOUR_GEMINI_API_KEY");

      // Get the image generation model
      const model = genAI.getGenerativeModel({ model: IMAGE_MODEL });

      setProgress(30);
      setLoadingMessage('Generating image with Gemini AI...');
      
      // --- Prompt Enhancement ---
      // Use the user's positive prompt directly without modification
      const enhancedPrompt = params.positive; 
      const hasInputImage = !!params.input_simage;
      // --- End Prompt Enhancement ---

      // Log the prompt being used
      console.log("Gemini Image Prompt:", enhancedPrompt); // Log the unmodified prompt

      // Prepare the content parts
      const contentParts = [{ text: enhancedPrompt }]; // Use enhanced prompt
      
      // Add image data if provided for editing
      if (hasInputImage) {
        // Assuming input_simage is a base64 data URL like "data:image/png;base64,..."
        const base64Data = params.input_simage.split(',')[1];
        const mimeType = params.input_simage.match(/data:(.*);base64/)?.[1] || 'image/png'; // Default to png if type extraction fails
        
        contentParts.push({
          inlineData: {
            mimeType: mimeType,
            data: base64Data
          }
        });
      }
      
      // Generate the image
      const result = await model.generateContent({
        contents: [{ role: "user", parts: contentParts }],
        generationConfig: {
          // Ensure responseModalities is set for image output
          responseModalities: ['Text', 'Image'], 
          temperature: 0.4,
          topK: 1,
          topP: 1,
          maxOutputTokens: 2048,
        },
      });
      
      setProgress(80);
      setLoadingMessage('Resim oluşturuluyor...');
      
      // Extract text and image data from the response
      const response = result.response;
      const images = [];
      let responseText = 'Resim başarıyla oluşturuldu!'; // Default text
      
      // Process the response parts
      if (response && response.candidates && response.candidates.length > 0) {
        const candidate = response.candidates[0];
        if (candidate.content && candidate.content.parts) {
          for (const part of candidate.content.parts) {
            if (part.text) {
              // Capture the text part of the response
              responseText = part.text; 
            } else if (part.inlineData && part.inlineData.data) {
              // This is the base64 image data
              const imageData = part.inlineData.data;
              const mimeType = part.inlineData.mimeType || 'image/png';
              
              // Upload the image to your storage
              const blob = base64ToBlob(`data:${mimeType};base64,${imageData}`, mimeType);
              const file = new File([blob], `gemini-generated-${Date.now()}.${mimeType.split('/')[1]}`, { type: mimeType });
              
              // Use the shared uploadImage function with the passed session
              const imageUrl = await uploadImage(file, session); // Use the session passed to the hook
              console.log('Uploaded image URL from Gemini:', imageUrl); // Add logging here
              if (imageUrl) {
                images.push(imageUrl);
              } else {
                console.error('Image upload failed or returned null URL for Gemini image.');
              }
            }
          }
        }
      }
      
      setProgress(100);
      setIsLoading(false);
      
      return {
        images,
        text: [responseText] // Use the captured text
      };
    } catch (error) {
      console.error('Error generating image with Gemini:', error);
      setIsLoading(false);
      throw error; // Removed duplicate throw
    }
  };

  // Generate text using Gemini API with conversation history and optional file data
  const generateText = async (prompt, systemInstruction = null, conversationHistory = [], fileData = null) => {
    setIsLoading(true);
    // Remove setProgress(10)
    setLoadingMessage('Thinking...'); // Keep a simple thinking message

    try {
      // Initialize the Gemini API
      const genAI = new GoogleGenerativeAI(process.env.REACT_APP_GEMINI_API_KEY || "YOUR_GEMINI_API_KEY");

      // Get the text generation model
      const model = genAI.getGenerativeModel({ model: TEXT_MODEL });

      // Remove setProgress(30) and setLoadingMessage('Generating response...')

      // Prepare generation config, including system instruction if provided
      const generationConfig = {
        // Standard text generation config (adjust as needed)
        temperature: 0.7,
        topK: 40,
        topP: 0.95,
        maxOutputTokens: 8192, // Increased token limit
      };

      // Format conversation history for Gemini API
      const formattedHistory = conversationHistory.map(msg => ({
        role: msg.sender === 'user' ? 'user' : 'model',
        parts: [{ text: msg.text }]
      }));

      // Prepare parts for the current user message (text + optional file)
      const currentUserParts = [{ text: prompt }];
      if (fileData && fileData.mimeType && fileData.base64Data) {
        currentUserParts.push({
          inlineData: {
            mimeType: fileData.mimeType,
            data: fileData.base64Data
          }
        });
      }

      // Add the current prompt (and file) as the latest user message
      formattedHistory.push({
        role: "user",
        parts: currentUserParts
      });

      const requestPayload = {
        contents: formattedHistory,
        generationConfig: generationConfig,
      };

      // Add system instruction if available
      if (systemInstruction) {
        requestPayload.systemInstruction = { parts: [{ text: systemInstruction }] };
      }

      // Generate text content
      const result = await model.generateContent(requestPayload);

      // Remove setProgress(100)
      setIsLoading(false); // Set loading false *before* returning

      // Extract text from the response
      const response = result.response;
      let responseText = "Sorry, I couldn't generate a response."; // Default error text

      if (response && response.candidates && response.candidates.length > 0) {
        const candidate = response.candidates[0];
        if (candidate.content && candidate.content.parts && candidate.content.parts.length > 0) {
          responseText = candidate.content.parts[0].text || responseText;
        }
      }

      return {
        text: responseText // Return just the text content
      };

    } catch (error) {
      console.error('Error generating text with Gemini:', error);
      setIsLoading(false);
      // Rethrow or return an error structure
      throw new Error(`Error generating text: ${error.message}`);
    }
  };


  // // Original ComfyUI workflow generation - REMOVED
  // const generateImageWithComfy = async (params) => { ... };

  // Function to translate text using Gemini (specifically for image prompts)
  const translateTextWithGemini = async (text, targetLang = 'en') => {
    // Avoid translation if text is empty or looks like English (basic check)
    // This regex checks for common English characters, numbers, and punctuation.
    // Adjust if needed for more complex scenarios or different source languages.
    if (!text || /^[a-zA-Z0-9\s.,!?'"“”‘’–—\-]*$/.test(text)) {
      console.log("Skipping translation for empty or likely English text:", text);
      return text;
    }

    console.log(`Attempting to translate "${text}" to ${targetLang}`);
    // Optionally update loading state specifically for translation if desired
    // setLoadingMessage(`Translating prompt to ${targetLang}...`);
    // setIsLoading(true);

    try {
      // Use a cost-effective and fast model for translation
      const translationGenAI = new GoogleGenerativeAI(process.env.REACT_APP_GEMINI_API_KEY || "YOUR_GEMINI_API_KEY");
      // Using gemini-1.5-flash as it's fast and efficient for this task
      const translationModel = translationGenAI.getGenerativeModel({ model: "gemini-1.5-flash-latest" });

      // Simple prompt instructing the model to translate and only return the result
      const prompt = `Translate the following text to ${targetLang}. Respond ONLY with the translated text, without any introductory phrases or explanations:\n\n"${text}"`;

      const result = await translationModel.generateContent(prompt);
      const response = result.response;
      let translatedText = text; // Default to original if translation fails

      if (response?.candidates?.[0]?.content?.parts?.[0]?.text) {
        translatedText = response.candidates[0].content.parts[0].text.trim();
        // Basic cleanup: remove potential quotes added by the model
        if (translatedText.startsWith('"') && translatedText.endsWith('"')) {
          translatedText = translatedText.substring(1, translatedText.length - 1);
        }
      } else {
         console.warn("Translation response format unexpected or empty.");
      }

      console.log(`Translation result: "${translatedText}"`);
      return translatedText;

    } catch (error) {
      console.error('Error translating text with Gemini:', error);
      // Return original text in case of error to avoid breaking the flow
      return text;
    } finally {
      // Reset translation-specific loading state if it was set
      // setIsLoading(false);
      // setLoadingMessage('Generating...');
    }
  };


  // Return generation and translation functions
  return {
    isLoading,
    progress,
    loadingMessage,
    generateImage, // For image generation
    generateText,  // For text generation
    translateTextWithGemini, // Add the new translation function
  };
};

// Memoize ChatComponent to prevent unnecessary re-renders on input change
const ChatComponent = React.memo(({ 
  containerStyle = {}, 
  session, 
  messages,
  setMessages,
  onGenerateImage, // Will be used when no text agent is active
  onGenerateText,  // Will be used when a text agent is active (with conversation history for context)
  translateTextWithGemini, // Receive the translation function
  currentAgent,    // Pass down the selected agent
  isLoading,
  progress,
  loadingMessage,
  imageDimensions = { width: 1024, height: 1024, upscaleBy: 1 },
  onSaveChat,
  isSaved = false,
  updateMessages, // Destructure the new prop
  uploadedFile,    // Receive state from parent
  setUploadedFile  // Receive setter from parent
}) => {
  const [input, setInput] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [generatedImages, setGeneratedImages] = useState([]);
  const [requestInProgress, setRequestInProgress] = useState(false); // Track if a request is in progress
  // const [currentAgent, setCurrentAgent] = useState(null); // Removed: currentAgent is now a prop
  // const [uploadedFile, setUploadedFile] = useState(null); // State is now managed by parent
  const chatWindowRef = useRef(null);
  const fileInputRef = useRef(null);
  const [warningOpen, setWarningOpen] = useState(false);
  const [warningMessage, setWarningMessage] = useState('');
  const recognitionRef = useRef(null);
  const [fullScreenImage, setFullScreenImage] = useState(null); // State for full-screen image URL
  const [isDragging, setIsDragging] = useState(false); // State for drag-and-drop visual feedback
  const theme = useTheme(); // Get theme for styling
  const navigate = useNavigate(); // Add useNavigate hook

  // State variables for credits
  const [creditsLeft, setCreditsLeft] = useState(0);
  const [creditsLoading, setCreditsLoading] = useState(true);
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const [creditAlertOpen, setCreditAlertOpen] = useState(false); // State for credit alert dialog
  const [requiredCredits, setRequiredCredits] = useState(0); // State for required credits
  const streamingIntervalRefs = useRef({}); // Ref to store active interval IDs for streaming messages

  // Define a smaller set of example prompts
  const examplePrompts = [
    // Generation Prompts
    "Modern oturma odası", // Shorter version
    "Deniz manzaralı villa", // More specific
    "Bahçeli ev (gündüz)", // Added detail
    // Editing Prompts
    "Mobilyaları kaldır", // General removal
    "Bitki ekle", // General addition
    "Duvar rengini değiştir", // General modification
  ];

  // Helper function to fetch an image URL and convert it to base64
  const imageUrlToBase64 = async (url) => {
    try {
      // Use fetch with appropriate headers if needed, especially for Supabase URLs
      const response = await fetch(url, { cache: 'no-cache' }); // Avoid caching issues
      if (!response.ok) {
        throw new Error(`HTTP error fetching image! status: ${response.status}`);
      }
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result); // reader.result is the data URL
        reader.onerror = (error) => {
          console.error("FileReader error:", error);
          reject(error);
        };
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error("Error fetching or converting image URL to base64:", error);
      setWarningMessage(`Görsel düzenleme için alınamadı: ${error.message}`);
      setWarningOpen(true);
      return null; // Handle error appropriately
    }
  };

  // Removed debouncedSetCurrentAgent as it's now handled directly in ChatTab
  // const debouncedSetCurrentAgent = useMemo(() => debounce((agent) => { ... }), [setMessages]);

  // Initialize Speech Recognition
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      console.warn('Speech Recognition API not supported in this browser.');
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = 'en-US';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setInput((prev) => prev + ' ' + transcript);
    };

    recognition.onerror = (event) => {
      console.error('Speech recognition error detected: ' + event.error);
      setIsListening(false);
      setWarningMessage('Speech recognition error.');
      setWarningOpen(true);
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    recognitionRef.current = recognition;

    // Cleanup intervals and timeouts on component unmount
    return () => {
      Object.values(streamingIntervalRefs.current).forEach(id => {
        clearInterval(id);
        clearTimeout(id);
      });
    };
  }, []);

  // Reference to track if we should scroll
  const shouldScrollRef = useRef(true);
  
  // Effect to scroll down chat window smoothly whenever messages change
  useEffect(() => {
    if (chatWindowRef.current && shouldScrollRef.current) {
      const { scrollHeight } = chatWindowRef.current;
      // Scroll to the bottom smoothly when messages update and scrolling is allowed
      chatWindowRef.current.scrollTo({
        top: scrollHeight,
        behavior: 'smooth' // Use smooth scrolling
      });
      
      // Add a data attribute to help identify the chat window for search scrolling
      chatWindowRef.current.setAttribute('data-chat-window', 'true');
    }
    
    // Reset the scroll flag after each update
    shouldScrollRef.current = true;
    
    // Trigger this effect when messages change
  }, [messages]);

  // Fetch subscription details and credits
  useEffect(() => {
    const fetchSubscriptionDetails = async () => {
      if (!session?.user?.id) return;
      
      try {
        setCreditsLoading(true);
        const details = await getSubscriptionDetails(session.user.id);
        setSubscriptionDetails(details);
        setCreditsLeft(details?.credits_remaining || 0);
      } catch (error) {
        console.error('Error fetching subscription details:', error);
        setWarningMessage('Subscription details could not be loaded.');
        setWarningOpen(true);
      } finally {
        setCreditsLoading(false);
      }
    };

    fetchSubscriptionDetails();
  }, [session]);

  // Ping mechanism to keep the connection alive
  const pingServer = async () => {
    try {
      await supabase.from('user_credits').select('count').limit(1);
    } catch (error) {
      console.error('Error pinging server:', error);
    }
  };

  useInterval(() => {
    pingServer();
  }, 60000); // Ping every 60 seconds

  if (!session) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Use the shared uploadImage function

  // Function to handle image download
  const handleDownload = (imageUrl) => {
    // For data URLs, we need to handle them differently
    if (imageUrl.startsWith('data:')) {
      // Create a blob from the data URL
      const parts = imageUrl.split(';base64,');
      const contentType = parts[0].replace('data:', '');
      const raw = window.atob(parts[1]);
      const rawLength = raw.length;
      const uInt8Array = new Uint8Array(rawLength);
      
      for (let i = 0; i < rawLength; ++i) {
        uInt8Array[i] = raw.charCodeAt(i);
      }
      
      const blob = new Blob([uInt8Array], { type: contentType });
      const blobUrl = URL.createObjectURL(blob);
      
      // Create a temporary anchor element
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `generated-image-${Date.now()}.png`;
      link.style.display = 'none';
      
      // Append to body, click, and remove
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      }, 100);
    } else {
      // For regular URLs, fetch the image first to handle CORS issues
      fetch(imageUrl)
        .then(response => response.blob())
        .then(blob => {
          const blobUrl = URL.createObjectURL(blob);
          
          // Create a temporary anchor element
          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = `generated-image-${Date.now()}.png`;
          link.style.display = 'none';
          
          // Append to body, click, and remove
          document.body.appendChild(link);
          link.click();
          
          // Clean up
          setTimeout(() => {
            document.body.removeChild(link);
            URL.revokeObjectURL(blobUrl);
          }, 100);
        })
        .catch(error => {
          console.error('Error downloading image:', error);
          alert('Failed to download image. Please try again.');
        });
    }
    
    // Prevent default navigation
    return false;
  };

  // Update handleSend to accept optional messageText for example prompts
  const handleSend = async (messageText = input) => {
    if (messageText.trim() === '' && !uploadedFile) return;

    // Prevent multiple simultaneous requests
    if (requestInProgress || isLoading) {
      setWarningMessage('Lütfen mevcut isteğin tamamlanmasını bekleyin.');
      setWarningOpen(true);
      return;
    }

    if (creditsLoading) {
      setWarningMessage('Krediler yükleniyor. Lütfen bekleyin.');
      setWarningOpen(true);
      return;
    }

    // Determine generation type and credit cost *before* checking creditsLeft
    const isTextAgent = currentAgent && currentAgent.type === AGENT_TYPE_TEXT;
    // TODO: Define credit costs more dynamically if needed
    const creditCost = isTextAgent ? 1 : 5; // Example: 1 credit for text, 5 for image

    // Check if user has enough credits for *this specific action*
    if (creditsLeft < creditCost) {
      setRequiredCredits(creditCost); // Set the required credits for the alert
      setCreditAlertOpen(true); // Open the credit alert dialog
      return; // Stop execution here
    }

    // --- If credits are sufficient, proceed ---

    const userMessageText = messageText; // Use provided messageText or default input
    const userFile = uploadedFile; // Store file state

    // Determine if we are in a sequential image editing flow
    // Check *before* adding the new user message
    const lastSystemMessageWithImage = [...messages]
      .reverse()
      .find(msg => msg.sender === 'system' && msg.images && msg.images.length > 0);

    // Create the base user message object
    const newMessage = {
      text: userMessageText,
      sender: 'user',
      timestamp: new Date(),
      // Conditionally attach the file ONLY if:
      // 1. It's a text agent OR
      // 2. It's an image agent AND it's the *first* image prompt (no prior system image)
      ...(userFile && (isTextAgent || !lastSystemMessageWithImage) && { file: userFile })
    };

    // Use the wrapper function to update messages and set dirty state
    updateMessages([...messages, newMessage]);
    // Clear input only if the default input was used (not an example prompt)
    if (messageText === input) {
      setInput(''); 
    }
    // Conditionally clear the uploaded file: only clear if it's a text agent
    if (isTextAgent) {
        setUploadedFile(null); // Clear file only for text agents
    }

    // Consume credits (isTextAgent and creditCost are already defined above)
    try {
      await consumeCredits(session.user.id, creditCost);
      setCreditsLeft(prev => prev - creditCost);
      // Refresh subscription details (optional, maybe less frequently)
      // const details = await getSubscriptionDetails(session.user.id);
      // setSubscriptionDetails(details);
    } catch (err) {
      console.error('Kredi tüketilirken hata:', err);
      setWarningMessage('Kredi tüketilirken hata: ' + err.message);
      setWarningOpen(true);
      // Revert adding the user message if credit consumption fails? Or allow message but show error?
      // For now, just show error and stop.
      return;
    }

    // --- Generation Logic ---
    try {
      setRequestInProgress(true); // Set flag at the start of request
      let result;
      if (isTextAgent) {
        // --- Text Generation ---
        // We'll use function calling to determine if this is a chart request
        // This regex is kept as a fallback
        const keywordBasedChartRequest = /grafik|grafiğ|çizelge|chart|plot|visualize|görselleştir|diagram|fiyat.*göster|fiyat.*grafik|değişim.*grafik|trend|istatistik|karşılaştır.*grafik/i.test(userMessageText);
        
        // Default to the regex result, but will be updated by function calling
        let isChartRequest = keywordBasedChartRequest;
        
        // Try to use function calling for more accurate chart intent detection
        try {
          // Initialize the Gemini API for intent detection
          const intentGenAI = new GoogleGenerativeAI(process.env.REACT_APP_GEMINI_API_KEY || "YOUR_GEMINI_API_KEY");
          const intentModel = intentGenAI.getGenerativeModel({ model: "gemini-1.5-flash" });
          
          // Define the chart detection function
          const chartDetectionFunction = {
            name: "create_chart",
            description: "Creates a chart or graph based on user request. Call this function when the user wants to visualize data, create a chart, modify a graph, or see statistics in visual form.",
            parameters: {
              type: "object",
              properties: {
                shouldCreateChart: {
                  type: "boolean",
                  description: "Whether the user's request requires creating or modifying a chart/graph."
                },
                chartType: {
                  type: "string",
                  enum: ["bar", "line", "pie", "doughnut"],
                  description: "The type of chart that would best represent the data requested."
                },
                dataDescription: {
                  type: "string",
                  description: "Brief description of what data should be visualized."
                }
              },
              required: ["shouldCreateChart"]
            }
          };
          
          // Call the model with function declaration
          const intentResponse = await intentModel.generateContent({
            contents: [{ role: "user", parts: [{ text: userMessageText }] }],
            generationConfig: {
              temperature: 0.1,
              maxOutputTokens: 1024,
            },
            tools: [{
              functionDeclarations: [chartDetectionFunction]
            }],
            toolConfig: {
              functionCallingConfig: {
                mode: "AUTO" // Let the model decide whether to call the function
              }
            }
          });
          
          // Check if the model called the function
          if (intentResponse.response.candidates && 
              intentResponse.response.candidates[0].content.parts && 
              intentResponse.response.candidates[0].content.parts[0].functionCall) {
            
            const functionCall = intentResponse.response.candidates[0].content.parts[0].functionCall;
            
            if (functionCall.name === "create_chart") {
              const args = functionCall.args;
              // Update isChartRequest based on the model's decision
              isChartRequest = args.shouldCreateChart;
              
              console.log("Function calling detected chart intent:", isChartRequest);
              console.log("Chart type suggestion:", args.chartType);
              console.log("Data description:", args.dataDescription);
            }
          }
        } catch (intentError) {
          console.error("Error using function calling for chart detection:", intentError);
          // Fall back to regex-based detection (already set above)
          console.log("Falling back to keyword-based chart detection:", isChartRequest);
        }
        
        // Add a thinking indicator message with a flag
        const thinkingMessage = {
          text: `Asistan (${currentAgent.name}) düşünüyor`, // Remove ellipsis here
          sender: 'system',
          timestamp: new Date(),
          isThinking: true // Add flag
        };
        updateMessages(prev => [...prev, thinkingMessage]); // Use updateMessages

        // Prepare file data if a file is uploaded AND its type is supported
        let fileDataForApi = null;
        let promptWithFileInfo = userMessageText; // Start with original text

        if (userFile && userFile.content && userFile.type) {
          // Extract mime type and base64 data from the data URL
          const match = userFile.content.match(/^data:(.+);base64,(.*)$/);
          if (match && match[1] && match[2]) {
            const mimeType = match[1];
            const base64Data = match[2];

            // Check if the MIME type is supported by Gemini for inlineData
            if (isMimeTypeSupportedByGemini(mimeType)) {
              fileDataForApi = {
                mimeType: mimeType,
                base64Data: base64Data
              };
              console.log(`Sending file ${userFile.name} (${mimeType}) as inlineData.`);
            } else if (userFile.extractedText) {
              // If we have extracted text from the document, include it in the prompt
              promptWithFileInfo += `\n\n[Attached file: ${userFile.name}, Type: ${mimeType}]\n\nFile Content:\n${userFile.extractedText}`;
              console.log(`Using extracted text from ${userFile.name} (${userFile.extractedText.length} characters)`);
            } else {
              // Type not supported for inlineData and no extracted text, add text reference instead
              promptWithFileInfo += `\n\n[Attached file: ${userFile.name}, Type: ${mimeType}]`;
              console.log(`File type ${mimeType} not directly supported by Gemini inlineData. Sending text reference for ${userFile.name}.`);
            }
          } else {
            console.warn("Could not parse base64 data URL for uploaded file:", userFile.name);
            // Add text reference even if parsing failed, as a fallback
            promptWithFileInfo += `\n\n[Attached file: ${userFile.name}, Type: ${userFile.type}]`;
            
            // If we have extracted text, still include it
            if (userFile.extractedText) {
              promptWithFileInfo += `\n\nFile Content:\n${userFile.extractedText}`;
            }
          }
        }

        // Filter messages to only include user and system messages (not loading indicators)
        const conversationHistory = messages.filter(msg =>
          msg.sender === 'user' ||
          (msg.sender === 'system' && !msg.isThinking && !msg.isStreaming && !msg.text.includes('oluşturuluyor')) // More robust filtering
        );

        // Modify the prompt if it's a chart request
        let finalPrompt = promptWithFileInfo;
        if (isChartRequest) {
          finalPrompt = `${promptWithFileInfo}\n\n
Bu istek için bir grafik oluşturman veya düzenlemen gerekiyor. Lütfen aşağıdaki adımları izle:

1. İstenen veri için gerçekçi değerler oluştur (örn. fiyatlar, oranlar, istatistikler)
2. Verileri JSON formatında döndür
3. Açıklamaları JSON dışında tut

Grafik verilerini kesinlikle aşağıdaki JSON formatında döndürmelisin:

\`\`\`json
{
  "type": "bar", 
  "data": { 
    "labels": ["Ocak 2023", "Şubat 2023", "Mart 2023"],
    "datasets": [
      {
        "label": "Örnek Veri",
        "data": [10, 20, 30],
        "backgroundColor": "#42A5F5"
      }
    ]
  }, 
  "options": {
    "title": {
      "text": "Örnek Grafik"
    }
  }
}
\`\`\`

Yukarıdaki örneği kullanıcının isteğine göre değiştir. Grafik türü olarak "bar", "line", "pie" veya "doughnut" kullanabilirsin.

ÖNEMLİ: 
1. Cevabında mutlaka geçerli bir JSON formatında veri olmalı
2. "Üzgünüm, grafik oluşturamıyorum" veya "araçlarım yok" gibi cevaplar verme
3. Kullanıcı "grafiği düzenle" veya benzeri bir istek yaptıysa, mevcut grafiği değiştir veya yeni bir grafik oluştur
4. İstenen konuyla ilgili gerçekçi veriler içeren bir grafik JSON'ı oluştur
5. JSON içinde JavaScript fonksiyonları kullanma (örneğin 'function(value) {...}' gibi), bunlar JSON formatında geçerli değildir
6. Yüzde işaretleri veya diğer birimler için, bunları doğrudan veri etiketlerine ekle veya grafik başlığında belirt
7. Pasta (pie) ve halka (doughnut) grafikleri için, verilerin toplamının anlamlı olduğundan emin ol
8. Tüm veri değerleri sayısal olmalı, string değerler kullanma`;
        }

        // Pass the potentially modified text prompt AND the file data (if supported)
        result = await onGenerateText(
          finalPrompt, // Use the prompt that might include chart instructions
          currentAgent.systemInstruction,
          conversationHistory,
          fileDataForApi // Pass null if type not supported, otherwise pass the data
        );
        console.log('Result from onGenerateText:', result);

        // --- Extract chart data if present ---
        let fullResponseText = result.text;
        let chartData = null;

        if (isChartRequest) {
          try {
            // Look for JSON in the response with multiple regex patterns
            // First try to find JSON between code blocks
            let jsonStr = null;
            let match = null;
            
            // Pattern 1: Look for ```json ... ``` blocks
            const codeBlockRegex = /```json\s*([\s\S]*?)\s*```/;
            match = fullResponseText.match(codeBlockRegex);
            
            if (match && match[1]) {
              jsonStr = match[1].trim();
            } else {
              // Pattern 2: Look for ```{ ... }``` blocks (without json keyword)
              const genericCodeBlockRegex = /```\s*(\{[\s\S]*?\})\s*```/;
              match = fullResponseText.match(genericCodeBlockRegex);
              
              if (match && match[1]) {
                jsonStr = match[1].trim();
              } else {
                // Pattern 3: Look for standalone JSON objects
                const standaloneJsonRegex = /\{[\s\S]*?"type"[\s\S]*?"data"[\s\S]*?\}/;
                match = fullResponseText.match(standaloneJsonRegex);
                
                if (match) {
                  jsonStr = match[0].trim();
                }
              }
            }
            
            if (jsonStr) {
              // Pre-process the JSON string to handle JavaScript functions
              // Replace function definitions with string placeholders
              const processedJsonStr = jsonStr.replace(/"callback"\s*:\s*function\s*\([^)]*\)\s*\{[^}]*\}/g, 
                '"callback": "function_placeholder"');
              
              try {
                // Try to parse the processed JSON
                const parsedData = JSON.parse(processedJsonStr);
                
                // Validate that it has the required structure
                if (parsedData && parsedData.type && parsedData.data) {
                  // Create a default chart if missing required properties
                  if (!parsedData.data.labels || !parsedData.data.datasets) {
                    console.warn('Chart data missing labels or datasets, using defaults');
                    parsedData.data.labels = parsedData.data.labels || ["Veri 1", "Veri 2", "Veri 3"];
                    parsedData.data.datasets = parsedData.data.datasets || [{
                      label: "Örnek Veri",
                      data: [10, 20, 30],
                      backgroundColor: "#42A5F5"
                    }];
                  }
                  
                  // Clean up any scales with function callbacks
                  if (parsedData.options && parsedData.options.scales) {
                    Object.keys(parsedData.options.scales).forEach(axis => {
                      if (parsedData.options.scales[axis].ticks && 
                          parsedData.options.scales[axis].ticks.callback === "function_placeholder") {
                        // Remove the callback function that can't be serialized
                        delete parsedData.options.scales[axis].ticks.callback;
                      }
                    });
                  }
                  
                  chartData = parsedData;
                  
                  // Remove the JSON from the text response
                  if (match) {
                    fullResponseText = fullResponseText.replace(match[0], '').trim();
                  }
                  
                  console.log('Successfully extracted chart data:', chartData);
                } else {
                  console.warn('Parsed JSON does not have required chart structure');
                }
              } catch (parseError) {
                console.error('Error parsing processed JSON:', parseError);
                
                // Try a more aggressive approach - extract just the essential parts
                try {
                  // Extract type, labels and datasets
                  const typeMatch = jsonStr.match(/"type"\s*:\s*"([^"]+)"/);
                  const labelsMatch = jsonStr.match(/"labels"\s*:\s*(\[[^\]]+\])/);
                  const datasetsMatch = jsonStr.match(/"datasets"\s*:\s*(\[[\s\S]*?\](?=\s*\}))/);
                  const titleMatch = jsonStr.match(/"text"\s*:\s*"([^"]+)"/);
                  
                  if (typeMatch && labelsMatch && datasetsMatch) {
                    // Manually construct a valid chart object
                    chartData = {
                      type: typeMatch[1],
                      data: {
                        labels: JSON.parse(labelsMatch[1]),
                        datasets: JSON.parse(datasetsMatch[1].replace(/,\s*\]/g, ']'))
                      },
                      options: {
                        title: titleMatch ? { text: titleMatch[1] } : { text: "Grafik" }
                      }
                    };
                    
                    console.log('Successfully extracted chart data using fallback method:', chartData);
                  }
                } catch (fallbackError) {
                  console.error('Fallback extraction also failed:', fallbackError);
                }
              }
            }
            
            // If we still don't have valid chart data, create a default chart
            if (!chartData) {
              console.warn('No valid JSON found in response for chart request');
              
              // If no JSON found but it's a chart request, create a default chart
              chartData = {
                type: "bar",
                data: {
                  labels: ["Veri Bulunamadı"],
                  datasets: [{
                    label: "Örnek Veri",
                    data: [0],
                    backgroundColor: "#42A5F5"
                  }]
                },
                options: {
                  title: {
                    text: "Grafik Verisi Oluşturulamadı"
                  }
                }
              };
              
              // Add a note to the response about the fallback chart
              fullResponseText += "\n\nNot: İstediğiniz grafik için veri oluşturulamadı, örnek bir grafik gösteriliyor.";
            }
          } catch (e) {
            console.error('Error parsing chart data:', e);
            
            // If parsing fails, create a default error chart
            chartData = {
              type: "bar",
              data: {
                labels: ["Hata"],
                datasets: [{
                  label: "Grafik Hatası",
                  data: [0],
                  backgroundColor: "#f44336"
                }]
              },
              options: {
                title: {
                  text: "Grafik Verisi İşlenemedi"
                }
              }
            };
            
            // Add error note to the response
            fullResponseText += "\n\nNot: Grafik verisi işlenirken bir hata oluştu, lütfen tekrar deneyin.";
          }
        }

        // --- Start Streaming Effect ---
        const tempId = `streaming-${Date.now()}`; // Unique ID for this streaming message
        const initialStreamMessage = {
          tempId: tempId,
          text: '', // Start with empty text
          sender: 'system',
          timestamp: new Date(),
          isStreaming: true,
          fullText: fullResponseText, // Store the full text
          chartData: chartData, // Add chart data if present
        };

        // Add the initial streaming message placeholder, removing the thinking message
        updateMessages(prev => [...prev.filter(m => !m.isThinking), initialStreamMessage]);

        // Start the interval to reveal text
        let charIndex = 0;
        const STREAMING_SPEED_MS = 22; // Normal speed
        const ACCELERATION_THRESHOLD_MS = 15000; // 15 seconds
        const startTime = Date.now(); // Record start time

        // Create a reference to track if we need to scroll
        const shouldScrollRef = { current: true };
        
        // Use requestAnimationFrame for smoother updates
        const updateStreamingText = () => {
          const currentTime = Date.now();
          const elapsedTime = currentTime - startTime;
          const isAccelerated = elapsedTime >= ACCELERATION_THRESHOLD_MS;

          // Determine how many characters to add this tick
          const charsToAddThisTick = isAccelerated ? 15 : 1; // Add 1 char normally, 15 chars when accelerated

          // Update charIndex, ensuring it doesn't exceed the total length
          charIndex = Math.min(charIndex + charsToAddThisTick, fullResponseText.length);

          const currentText = fullResponseText.substring(0, charIndex);
          const isDone = charIndex >= fullResponseText.length;

          // Update messages without triggering scroll effect
          shouldScrollRef.current = false;
          
          updateMessages(prevMessages => {
            return prevMessages.map(msg => {
              if (msg.tempId === tempId) {
                return {
                  ...msg,
                  text: currentText,
                  isStreaming: !isDone, // Set isStreaming to false only when truly done
                };
              }
              return msg;
            });
          });

          // If streaming is complete
          if (isDone) {
            // Final scroll to bottom when complete
            shouldScrollRef.current = true;
            clearInterval(streamingIntervalRefs.current[tempId]);
            delete streamingIntervalRefs.current[tempId];
            return;
          }
          
          // Continue animation
          streamingIntervalRefs.current[tempId] = setTimeout(updateStreamingText, STREAMING_SPEED_MS);
        };
        
        // Start the animation
        streamingIntervalRefs.current[tempId] = setTimeout(updateStreamingText, STREAMING_SPEED_MS);
        // --- End Streaming Effect ---

      } else {
        // --- Image Generation (Default) ---
        const systemThinkingMessage = {
          text: `Görsel oluşturuluyor (Gemini AI), lütfen bekleyin...`,
          sender: 'system',
          timestamp: new Date()
        };
        setMessages(prev => [...prev, systemThinkingMessage]); // Add thinking message without marking dirty
        // setIsChatDirty(true); // No need to mark dirty here

        // --- Translate prompt to English if it's an image agent ---
        let promptForGeneration = userMessageText;
        try {
          // Call the translation function passed via props
          promptForGeneration = await translateTextWithGemini(userMessageText, 'en');
          console.log("Using translated prompt for image generation:", promptForGeneration);
        } catch (translationError) {
          console.error("Failed to translate prompt, using original:", translationError);
          // Optionally show a warning to the user
          setWarningMessage("Prompt çevrilemedi, orijinal metin kullanılıyor.");
          setWarningOpen(true);
          // promptForGeneration remains userMessageText (original)
        }
        // --- End Translation ---

        // --- Determine input image for editing (Revised Logic) ---
        let inputImageBase64 = null;
        let usedUploadedFile = false; // Flag to track if the uploaded file was used

        // PRIORITY 1: Use the newly uploaded file if available
        if (userFile && userFile.type.startsWith('image/')) {
           console.log("Using newly uploaded file as input:", userFile.name);
           inputImageBase64 = userFile.content; // Assumes userFile.content is already base64 data URL
           usedUploadedFile = true; // Mark that the uploaded file was used
        }
        // PRIORITY 2: If no new file uploaded, try sequential editing using the last generated image
        else {
            const lastSystemMessageWithImage = [...messages] // Get current messages before adding the thinking message
              .reverse()
              .find(msg => msg.sender === 'system' && msg.images && msg.images.length > 0);

            if (lastSystemMessageWithImage) {
              const lastImageUrl = lastSystemMessageWithImage.images[0];
              console.log("Attempting sequential edit using last generated image:", lastImageUrl);
              inputImageBase64 = await imageUrlToBase64(lastImageUrl);
              if (!inputImageBase64) {
                 console.warn("Failed to convert last image URL to base64 for sequential edit.");
              } else {
                 console.log("Successfully obtained base64 for sequential edit.");
              }
            } else {
               console.log("No uploaded file and no previous system image found for editing.");
            }
        }
        // --- End Determine input image ---

        // --- Add prefix if editing an image ---
        let finalPrompt = promptForGeneration;
        if (inputImageBase64) {
          finalPrompt = `Please edit the picture and ${promptForGeneration}`;
          console.log("Prefixed prompt for image editing:", finalPrompt);
        }
        // --- End Add prefix ---

        const generationParams = {
          positive: finalPrompt, // Use the potentially prefixed prompt
          width: imageDimensions.width,
          height: imageDimensions.height,
          // Add other relevant params if needed
        };

        // Add the determined image content (inputImageBase64) to generation params if available
        if (inputImageBase64) {
          generationParams.input_simage = inputImageBase64;
        } else {
           // Log if neither an uploaded file nor a previous image was used for editing
           console.log("Proceeding with image generation without an input image for editing.");
        }

        result = await onGenerateImage(generationParams);
        console.log('Result from onGenerateImage:', result);

        const generatedImageUrls = result.images || [];
        const generatedText = result.text && result.text.length > 0 ? result.text[0] : "Görsel başarıyla oluşturuldu!";
        setGeneratedImages(prev => [...prev, ...generatedImageUrls]); // Keep track of generated images

        const response = {
          text: generatedText,
          sender: 'system',
          timestamp: new Date(),
          images: generatedImageUrls
        };
        // Use wrapper to add response and mark dirty
        updateMessages(prev => [...prev.filter(m => !m.text.includes('oluşturuluyor')), response]);

        // Clear the uploaded file *if* it was the one used for this image operation
        if (usedUploadedFile) {
            setUploadedFile(null);
            console.log("Cleared the uploaded file after using it for image generation/editing.");
        }
      }

    } catch (error) {
      console.error('Error processing generation request:', error);
      const errorResponse = {
        text: `İstek işlenirken bir hata oluştu: ${error.message}. Lütfen tekrar deneyin.`,
        sender: 'system',
        timestamp: new Date()
      };
      // Use wrapper to add error response and mark dirty
      updateMessages(prev => [...prev.filter(m => !m.text.includes('oluşturuluyor') && !m.text.includes('düşünüyor...')), errorResponse]);
    } finally {
      setRequestInProgress(false); // Clear flag regardless of success or failure
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      handleFile(file);
      // Reset the input value to allow selecting the same file again
      event.target.value = null; 
    }
  };

  const handleFile = async (inputFile) => {
    if (!inputFile) return;

    let fileToProcess = inputFile;
    let originalFileName = inputFile.name;
    let conversionError = null;
    let extractedText = null;

    try {
      // --- HEIC/HEIF Conversion Step ---
      const fileNameLower = inputFile?.name?.toLowerCase() || '';
      const isHeicExtension = fileNameLower.endsWith('.heic') || fileNameLower.endsWith('.heif');
      const isHeicType = inputFile.type === 'image/heic' || inputFile.type === 'image/heif';

      if (isHeicType || (!inputFile.type && isHeicExtension)) {
        console.log(`Attempting HEIC/HEIF to JPEG conversion for chat: ${originalFileName}`);
        try {
          const arrayBuffer = await inputFile.arrayBuffer();
          const uint8Array = new Uint8Array(arrayBuffer);
          const outputBuffer = await convert({
            buffer: uint8Array,
            format: 'JPEG',
            quality: 0.9
          });
          const jpegFileName = originalFileName.replace(/\.(heic|heif)$/i, '.jpg');
          fileToProcess = new File([outputBuffer], jpegFileName, { type: 'image/jpeg' });
          console.log(`Created new File object for converted JPEG in chat: ${jpegFileName}`);
        } catch (convError) {
          console.error(`HEIC conversion failed for chat file ${originalFileName}:`, convError);
          conversionError = convError;
          fileToProcess = inputFile; // Use original if conversion fails
          alert(`HEIC dosyası JPEG'e dönüştürülemedi: ${convError.message}. Orijinal dosya kullanılacak.`);
        }
      } else {
        console.log(`Chat file ${originalFileName} is not HEIC/HEIF, proceeding directly.`);
      }

      // --- Document Text Extraction ---
      if (fileToProcess.type === 'application/pdf') {
        try {
          console.log(`Processing PDF: ${fileToProcess.name}`);
          
          // Instead of extracting text directly, we'll add a note about the PDF
          extractedText = `[PDF Document: ${fileToProcess.name}]\n\nThis is a PDF document that has been uploaded. Please analyze the content and respond to any questions about it.`;
          
          // We'll still try to extract some text using the FileReader as a fallback
          try {
            console.log("Attempting basic PDF text extraction");
            const reader = new FileReader();
            const textPromise = new Promise((resolve, reject) => {
              reader.onload = () => resolve(reader.result);
              reader.onerror = reject;
            });
            
            reader.readAsText(fileToProcess);
            const text = await textPromise;
            
            // If we got some text content, append it
            if (text && text.length > 100) { // Ensure we have meaningful content
              extractedText += `\n\nExtracted Content:\n${text}`;
              console.log(`Successfully extracted ${text.length} characters from PDF using basic method`);
            } else {
              console.log("Basic extraction didn't yield meaningful text");
              extractedText += "\n\nNote: Could not extract readable text from this PDF. It may be a scanned document or contain images.";
            }
          } catch (fallbackError) {
            console.error(`PDF text extraction failed: ${fallbackError.message}`);
            extractedText += "\n\nNote: Could not extract text from this PDF due to technical limitations.";
          }
        } catch (pdfError) {
          console.error(`Error processing PDF: ${pdfError.message}`);
          extractedText = `[PDF Document: ${fileToProcess.name}]\n\nThis PDF could not be processed due to an error.`;
        }
      } else if (fileToProcess.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' || 
                 fileToProcess.type === 'application/msword') {
        // For Word documents, we'll just note that we can't extract text directly
        console.log(`Word document detected: ${fileToProcess.name}. Text extraction not supported directly.`);
      } else if (fileToProcess.type === 'text/plain') {
        try {
          // For text files, read the content directly
          const text = await fileToProcess.text();
          extractedText = text;
          console.log(`Successfully extracted ${text.length} characters from text file`);
        } catch (textError) {
          console.error(`Error reading text file: ${textError.message}`);
        }
      }

      // --- Read File (Original or Converted) as Base64 ---
      const reader = new FileReader();
      reader.onload = (e) => {
        setUploadedFile({
          name: fileToProcess.name, // Use the name of the processed file
          type: fileToProcess.type, // Use the type of the processed file
          content: e.target.result, // Store the base64 data URL
          extractedText: extractedText // Store extracted text if available
        });
      };
      reader.onerror = (error) => {
        console.error("Error reading file:", error);
        setWarningMessage('Dosya okunurken hata oluştu.');
        setWarningOpen(true);
      };
      reader.readAsDataURL(fileToProcess); // Read the potentially converted file

    } catch (error) {
      console.error("Error during file processing in chat:", error);
      const message = conversionError
        ? `HEIC dönüştürme başarısız oldu: ${conversionError.message}`
        : `Dosya işlenemedi: ${error.message}`;
      alert(message);
    }

    // Note: We are no longer immediately uploading the file to Supabase here.
    // The base64 data URL stored in uploadedFile.content will be used by Gemini.
    // If the Supabase URL is needed elsewhere, the upload logic might need adjustment.
  };

  const removeUploadedFile = () => {
    setUploadedFile(null);
  };

  // --- Drag and Drop Handlers ---
  const handleDragEnter = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    // Check if the leave event is heading towards a child element
    if (event.relatedTarget && event.currentTarget.contains(event.relatedTarget)) {
      return; // Don't set isDragging false if moving within the drop zone
    }
    setIsDragging(false);
  };

  const handleDragOver = (event) => {
    event.preventDefault(); // Necessary to allow dropping
    event.stopPropagation();
    setIsDragging(true); // Keep dragging state true while over
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);

    if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
      const file = event.dataTransfer.files[0];

      const fileNameLower = file.name?.toLowerCase() || '';
      const isHeic = fileNameLower.endsWith('.heic') || fileNameLower.endsWith('.heif');
      const isAvif = fileNameLower.endsWith('.avif') || file.type === 'image/avif';
      const isGif = fileNameLower.endsWith('.gif') || file.type === 'image/gif';
      const isDocument = file.type.startsWith('application/') || file.type === 'text/plain' || 
                         file.name.endsWith('.pdf') || file.name.endsWith('.doc') || 
                         file.name.endsWith('.docx') || file.name.endsWith('.txt') || 
                         file.name.endsWith('.rtf');

      // Define allowed image types (excluding GIF and AVIF)
      const isAllowedImageType = file.type.startsWith('image/') && !isAvif && !isGif;
      const isAllowedHeic = file.type === 'image/heic' || file.type === 'image/heif' || (!file.type && isHeic);

      // Allow standard image types (excluding GIF/AVIF), HEIC/HEIF, or documents
      if (isAllowedImageType || isAllowedHeic || isDocument) {
        handleFile(file); // Use the existing file handling logic
      } else {
        // Log the actual type received for debugging
        console.warn(`Unsupported file type dropped in chat: Name='${file.name}', Type='${file.type}'`);
        let message = `Desteklenmeyen dosya türü: ${file.type || file.name}. Lütfen JPEG, PNG, WebP, HEIC, HEIF, PDF, DOC, veya TXT dosyası yükleyin.`;
        if (isAvif) {
          message = `AVIF dosyaları şu anda desteklenmiyor. Lütfen farklı bir format deneyin.`;
        } else if (isGif) {
          message = `Animasyonlu GIF dosyaları desteklenmiyor. Lütfen statik bir resim formatı (JPEG, PNG, WebP, HEIC) veya belge deneyin.`;
        }
        alert(message);
      }

      event.dataTransfer.clearData(); // Clean up
    }
  };
  // --- End Drag and Drop Handlers ---

  const handleVoiceInput = () => {
    if (!recognitionRef.current) {
      setWarningMessage('Ses tanıma desteklenmiyor.');
      setWarningOpen(true);
      return;
    }

    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      try {
        recognitionRef.current.start();
        setIsListening(true);
      } catch (error) {
        console.error('Error starting speech recognition:', error);
        setWarningMessage('Ses girişi başlatılırken hata oluştu.');
        setWarningOpen(true);
      }
    }
  };

  const handleWarningClose = () => {
    setWarningOpen(false);
    setWarningMessage('');
  };

  // Handle closing the credit alert dialog
  const handleCreditAlertClose = () => {
    setCreditAlertOpen(false);
    // Navigate to subscription page when the button is clicked
    navigate('/subscription'); 
  };

  // File display component
  const FileDisplay = ({ file }) => {
    if (!file) return null;

    if (file.type.startsWith('image/')) {
      // Check if content is a base64 string or starts with "data:"
      const isBase64OrDataUrl = file.content && (
        file.content.startsWith('data:') || 
        file.content.startsWith('/9j/') || // Common JPEG base64 start
        file.content.startsWith('iVBOR') || // Common PNG base64 start
        /^[A-Za-z0-9+/=]+$/.test(file.content.substring(0, 100)) || // Check if it looks like base64 (expanded check)
        (file.content.length > 500 && !file.content.includes('http://') && !file.content.includes('https://')) // Long string that's not a URL
      );
      
      // Create proper src URL
      let imgSrc;
      if (isBase64OrDataUrl && !file.content.startsWith('data:')) {
        // Add data URL prefix if it's raw base64
        imgSrc = `data:${file.type};base64,${file.content}`;
      } else if (file.content.startsWith('http') && file.content.includes('supabase.co') && file.content.includes('chat-files')) {
        // This is likely a URL to a base64 string stored in Supabase
        // We'll handle this with an error fallback
        imgSrc = file.content;
      } else {
        // Use as-is if it's already a data URL or regular URL
        imgSrc = file.content;
      }
      
      return (
        // Removed maxHeight and overflow: 'hidden' to allow full image height
        <Box sx={{ mt: 1, maxWidth: '10.3125rem' }}> {/* 165px to rem */}
          <img 
            src={imgSrc} 
            alt={file.name} 
            style={{ 
              width: '100%', 
              height: '100%', 
              objectFit: 'cover', 
              borderRadius: '8px' 
            }}
            onError={(e) => {
              // If the image fails to load and it's a Supabase URL, it might be base64 content
              if (imgSrc.startsWith('http') && imgSrc.includes('supabase.co')) {
                // Fetch the content and convert it to a data URL
                fetch(imgSrc, {
                  headers: {
                    // Request uncompressed content to avoid Brotli decoding issues
                    'Accept-Encoding': 'identity'
                  },
                  // Bypass cache to get fresh content
                  cache: 'no-cache'
                })
                  .then(response => {
                    // Check content type to handle appropriately
                    const contentType = response.headers.get('content-type');
                    if (contentType && contentType.includes('text/plain')) {
                      return response.text();
                    } else if (contentType && contentType.includes('image/')) {
                      // If it's already an image, use the URL directly
                      e.target.src = imgSrc;
                      return null;
                    }
                    return response.text();
                  })
                  .then(text => {
                    if (!text) return; // Skip if we already handled the image
                    
                    // Check if the response is base64 data
                    if (text && (
                      text.startsWith('/9j/') || 
                      text.startsWith('iVBOR') || 
                      text.includes('data:image') ||
                      /^[A-Za-z0-9+/=]+$/.test(text.substring(0, 100))
                    )) {
                      // If it already has the data:image prefix
                      if (text.startsWith('data:image')) {
                        e.target.src = text;
                      } else {
                        // Set the src to a data URL with the base64 content
                        e.target.src = `data:${file.type};base64,${text}`;
                      }
                    }
                  })
                  .catch(err => {
                    console.error('Error fetching image content:', err);
                    // Try one more time with a different approach as fallback
                    const xhr = new XMLHttpRequest();
                    xhr.onload = function() {
                      if (xhr.status === 200) {
                        const text = xhr.responseText;
                        if (text && (text.startsWith('/9j/') || text.startsWith('iVBOR'))) {
                          e.target.src = `data:${file.type};base64,${text}`;
                        }
                      }
                    };
                    xhr.open('GET', imgSrc);
                    xhr.setRequestHeader('Accept-Encoding', 'identity');
                    xhr.send();
                  });
              }
            }}
          />
        </Box>
      );
    } else {
      const getFileEmoji = (fileType) => {
        if (fileType.includes('pdf')) return '📄';
        if (fileType.includes('doc')) return '📝';
        if (fileType.includes('txt')) return '📃';
        return '📎';
      };

      return (
        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ mr: 1 }}>{getFileEmoji(file.type)}</Typography>
          <Typography variant="body2">{file.name}</Typography>
        </Box>
      );
    }
  };

  return (
    <>
      {/* Inject CSS for Animated Ellipsis */}
      <style>{AnimatedEllipsisStyle}</style>
      {/* Main container with drag-and-drop handlers and visual feedback */}
      <Box 
        sx={{
          ...containerStyle,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          width: '100%',
          minWidth: 0,
          alignSelf: 'stretch',
          maxHeight: '100vh',
          overflow: 'hidden',
          position: 'relative', // Needed for overlay positioning
          border: isDragging ? `2px dashed ${theme.palette.primary.main}` : 'none', // Visual feedback
          backgroundColor: isDragging ? alpha(theme.palette.primary.main, 0.1) : 'transparent', // Subtle background change
          transition: 'border 0.2s ease-in-out, background-color 0.2s ease-in-out', // Smooth transition
        }}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {/* Optional: Add a visual overlay when dragging */}
        {isDragging && (
          <Box sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: alpha(theme.palette.background.default, 0.5), // Semi-transparent overlay
            zIndex: 5, // Ensure it's above chat content but below input
            pointerEvents: 'none', // Allow underlying elements to be targeted for dragLeave etc.
          }}>
            <Typography variant="h6" color="primary">Drop file here</Typography>
          </Box>
        )}
        {/* Original content starts here */}
        <Box 
          ref={chatWindowRef}
          sx={{ 
            flex: 1, 
            overflowY: 'auto', 
            overflowX: 'hidden', // Prevent horizontal scrolling
            p: 2, 
            mb: 2,
            maxHeight: 'calc(100vh - 16rem)', // Reduce height to make room for input area
            pb: '4rem', // Add padding at the bottom to ensure messages aren't hidden behind input
          }}
          data-chat-window="true" // Add data attribute to help identify this element
        >
          {messages.map((message, index) => (
            <Fade in={true} key={index}>
              <Box 
                sx={{ 
                  mb: 2, 
                  display: 'flex', 
                  flexDirection: 'column',
                  alignItems: message.sender === 'user' ? 'flex-end' : 'flex-start' 
                }}
              >
                <MessageBubble 
                  sender={message.sender}
                  className="message-bubble" // Add class for search navigation
                >
                  {/* Message options menu */}
                  {!message.isThinking && !message.isStreaming && (
                    <Box sx={{ 
                      position: 'absolute', 
                      top: '8px', 
                      right: message.sender === 'user' ? '8px' : '8px',
                      zIndex: 2
                    }}>
                      <MessageOptionsMenu message={message} />
                    </Box>
                  )}
                  
                  {/* Render thinking indicator or markdown */}
                  {message.isThinking ? (
                    <Typography variant="body1" component="span">
                      {message.text}
                      <AnimatedEllipsis /> 
                    </Typography>
                  ) : (
                    <>
                      {/* Render the actual message text using ReactMarkdown */}
                      <ReactMarkdown components={{ 
                        p: ({node, ...props}) => <Typography variant="body1" component="p" {...props} />,
                        strong: ({node, ...props}) => <Typography variant="body1" component="strong" sx={{ fontWeight: 'bold' }} {...props} />,
                        ul: ({node, ...props}) => <ul style={{ paddingLeft: '20px', margin: '8px 0' }} {...props} />,
                        li: ({node, ...props}) => <li style={{ marginBottom: '4px' }}><Typography variant="body1" component="span" {...props} /></li>,
                      }}>
                        {typeof message.text === 'string' ? message.text : ''}
                      </ReactMarkdown>
                      
                      {/* Display chart if present */}
                      {message.chartData && <ChartDisplay chartData={message.chartData} />}
                      
                      {/* Display quick prompts only for initial system messages */}
                      {message.isInitialMessage && message.quickPrompts && message.sender === 'system' && (
                        <Box sx={{ 
                          mt: 2, 
                          display: 'flex', 
                          flexWrap: 'wrap', 
                          gap: 1,
                          animation: 'fadeIn 0.5s ease-out'
                        }}>
                          {message.quickPrompts.map((prompt, idx) => (
                            <Button
                              key={idx}
                              variant="outlined"
                              size="small"
                              onClick={() => handleSend(prompt)}
                              sx={{
                                borderRadius: '20px',
                                borderColor: 'transparent',
                                color: 'primary.main',
                                backgroundColor: alpha(theme.palette.primary.main, 0.08),
                                '&:hover': {
                                  backgroundColor: alpha(theme.palette.primary.main, 0.15),
                                  borderColor: 'transparent',
                                  transform: 'translateY(-2px)',
                                },
                                transition: 'all 0.3s ease',
                                boxShadow: '0px 2px 4px rgba(0,0,0,0.05)',
                                fontWeight: 500,
                                textTransform: 'none',
                                px: 2,
                                py: 0.5,
                                fontSize: '0.85rem'
                              }}
                            >
                              {prompt}
                            </Button>
                          ))}
                        </Box>
                      )}
                    </>
                  )}
                  {/* FileDisplay and Timestamp */}
                  <FileDisplay file={message.file} />
                  {/* Only show timestamp when not streaming or thinking */}
                  {!message.isStreaming && !message.isThinking && (
                    <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary', fontSize: '0.7rem' }}>
                      {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </Typography>
                  )}
                </MessageBubble>
                {/* Updated Image Display */}
                {message.images && (
                  <Box sx={{ 
                    display: 'flex', 
                    flexWrap: 'wrap', 
                    // Align thumbnails to the start (left for system messages)
                    justifyContent: message.sender === 'user' ? 'flex-end' : 'flex-start', 
                    mt: 1, 
                    maxWidth: '70%' 
                  }}>
                    {message.images.map((imageUrl, imgIndex) => (
                      <Box 
                        key={imgIndex} 
                        sx={{ 
                          position: 'relative',
                          width: '10.3125rem', // 165px to rem
                          height: '10.3125rem', // 165px to rem
                          m: 0.5, // Margin around thumbnail
                          borderRadius: '0.5rem', // 8px to rem
                          overflow: 'hidden', 
                          boxShadow: '0 0.125rem 0.25rem rgba(0,0,0,0.1)', // 2px 4px to rem
                          '&:hover .image-actions': { // Show actions on hover
                            opacity: 1,
                          }
                        }}
                      >
                        <img 
                          src={imageUrl} 
                          alt={`Generated image ${imgIndex + 1}`} 
                          style={{ 
                            width: '100%', 
                            height: '100%', 
                            objectFit: 'cover', 
                            display: 'block' // Prevent extra space below image
                          }}
                        />
                        {/* Overlay for actions */}
                        <Box 
                          className="image-actions"
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            backgroundColor: 'rgba(0, 0, 0, 0.4)',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            opacity: 0, // Hidden by default
                            transition: 'opacity 0.2s ease-in-out',
                          }}
                        >
                          <IconButton 
                            size="small" 
                            onClick={() => setFullScreenImage(imageUrl)}
                            sx={{ color: 'white', mx: 0.5 }}
                          >
                            <ZoomInIcon fontSize="small" />
                          </IconButton>
                          <IconButton 
                            size="small" 
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleDownload(imageUrl);
                              return false;
                            }}
                            sx={{ color: 'white', mx: 0.5 }}
                          >
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </Fade>
          ))}
          {/* Only show loading indicator for non-text agents (e.g., image generation) */}
          {isLoading && currentAgent?.type !== AGENT_TYPE_TEXT && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column', mt: 2 }}>
              <CircularProgress size={24} />
              <Typography variant="caption" sx={{ mt: 1, color: 'text.secondary' }}>
                {loadingMessage} {progress.toFixed(0)}%
              </Typography>
            </Box>
          )}
        </Box>
        
        {/* Save button */}
        {messages.length > 1 && (
          <Box sx={{ px: 2, display: 'flex', justifyContent: 'flex-end', position: 'relative', bottom: '2rem' }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={isSaved ? <CheckIcon /> : <SaveIcon />}
              onClick={onSaveChat}
              sx={{ mb: 1 }}
              color={isSaved ? "success" : "primary"}
            >
              {isSaved ? 'Kaydedildi' : 'Sohbeti Kaydet'}
            </Button>
          </Box>
        )}
        
        <Divider />
        
        <Box sx={{ 
          p: 2,
          position: 'sticky',
          bottom: '3rem', // Move up from bottom
          backgroundColor: theme => theme.palette.background.default,
          zIndex: 10,
          borderTop: '1px solid',
          borderColor: 'divider',
          mt: 'auto', // Push to bottom
          marginBottom: '1rem' // Add space at the bottom
        }}>
          {/* Example Prompts Section - Only for Image Agents */}
          {currentAgent.type === AGENT_TYPE_IMAGE && (
            <Box sx={{ 
              mb: 2, 
              display: 'flex', 
              flexWrap: 'wrap', // Allow wrapping on smaller screens
              gap: 1, // Use gap for spacing like quick prompts
              pb: 1 
            }}>
              {examplePrompts.map((prompt, index) => (
                <Button
                  key={index}
                  variant="outlined"
                  size="small"
                  onClick={() => handleSend(prompt)} // Call handleSend with the prompt text
                  sx={{
                    // Style copied from quick prompts in message bubble
                    borderRadius: '20px',
                    borderColor: 'transparent',
                    color: 'primary.main',
                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.15),
                      borderColor: 'transparent',
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.3s ease',
                    boxShadow: '0px 2px 4px rgba(0,0,0,0.05)',
                    fontWeight: 500,
                    textTransform: 'none',
                    px: 2,
                    py: 0.5,
                    fontSize: '0.85rem',
                    // Removed whiteSpace: 'nowrap' to allow text wrapping
                    // Removed mr: 1, using gap in parent Box instead
                  }}
                >
                  {prompt}
                </Button>
              ))}
            </Box>
          )}

          {/* Guidance Message - Only for Image Agents */}
          {currentAgent.type === AGENT_TYPE_IMAGE && (
            <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
              Resmi düzenlemek için bir resim yükleyin veya yeni bir resim oluşturmak için bir prompt girin.
            </Typography>
          )}

          {/* Existing Uploaded File Display */}
          {uploadedFile && (
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              {uploadedFile.type.startsWith('image/') ? (
                <Box 
                  sx={{ 
                    width: '3.75rem', // 60px to rem
                    height: '3.75rem', // 60px to rem
                    mr: 1, 
                    position: 'relative',
                    borderRadius: '0.5rem', // 8px to rem
                    overflow: 'hidden',
                    boxShadow: '0 0.0625rem 0.1875rem rgba(0,0,0,0.1)', // 1px 3px to rem
                  }}
                >
                  <img 
                    src={uploadedFile.content} 
                    alt="Uploaded file"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                  <IconButton 
                    size="small" 
                    onClick={removeUploadedFile}
                    sx={{ 
                      position: 'absolute', 
                      top: 0, 
                      right: 0, 
                      backgroundColor: 'rgba(255,255,255,0.8)',
                      padding: '2px',
                      '&:hover': {
                        backgroundColor: 'rgba(255,255,255,0.9)',
                      }
                    }}
                  >
                    <CloseIcon fontSize="small" sx={{ fontSize: '0.8rem' }} />
                  </IconButton>
                </Box>
              ) : (
                <>
                  <Typography variant="body2" sx={{ mr: 1 }}>
                    {uploadedFile.name}
                  </Typography>
                  <IconButton size="small" onClick={removeUploadedFile}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </>
              )}
            </Box>
          )}
          
          {/* Adjusted Box for multiline input and button alignment */}
          <Box sx={{ display: 'flex', alignItems: 'flex-end' }}> 
            {/* Promptlarım Button Removed */}
            
            <StyledTextField
              fullWidth
              multiline // Allow multiple lines
              maxRows={4} // Allow text field to grow up to 4 lines
              variant="outlined"
              placeholder="Size bugün nasıl yardımcı olabilirim?"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={(e) => {
                // Send on Enter unless Shift is pressed
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault(); // Prevent default newline behavior
                  handleSend();
                }
              }}
              InputProps={{
                endAdornment: (
                  // Center the icons vertically
                  <InputAdornment position="end" sx={{ alignItems: 'center' /* Removed mb: 1 */ }}>
                    {/* Hidden file input, correctly placed */}
                    <input
                      type="file"
                      ref={fileInputRef}
                      style={{ display: 'none' }}
                      onChange={handleFileUpload}
                      accept="image/jpeg,image/png,image/webp,.heic,.heif,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,.pdf,.doc,.docx,.txt,.rtf" // Explicitly list supported image types + docs
                    />
                    {/* Removed misplaced sx prop and ref from here */}
                    <IconButton 
                      onClick={() => fileInputRef.current.click()}
                      disabled={creditsLeft <= 0}
                      size="small"
                      sx={{ color: 'text.secondary' }}
                    >
                      <AttachFileIcon fontSize="small" />
                    </IconButton>
                    <IconButton 
                      onClick={handleVoiceInput}
                      disabled={!('SpeechRecognition' in window) && !('webkitSpeechRecognition' in window) || creditsLeft <= 0}
                      size="small"
                      color={isListening ? "primary" : "default"}
                      sx={{ color: isListening ? 'primary.main' : 'text.secondary' }}
                    >
                      <MicIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      onClick={() => handleSend()} // Explicitly call handleSend in an arrow function
                      disabled={isLoading || requestInProgress || creditsLeft <= 0 || (!input.trim() && !uploadedFile)}
                      color="primary"
                      size="small"
                    >
                      <SendIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
                // Adjust padding for multiline input
                sx: { 
                  py: 1, // Reduce vertical padding inside the input
                  '& .MuiOutlinedInput-input': {
                    paddingTop: '8px', // Adjust top padding for multiline
                    paddingBottom: '8px', // Adjust bottom padding for multiline
                  }
                } 
              }}
              disabled={creditsLeft <= 0}
            />
          </Box>
        </Box>
        
        {/* Removed duplicated input section */}
        
        <Snackbar
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          open={warningOpen}
          autoHideDuration={3000}
          onClose={handleWarningClose}
        >
          <Alert onClose={handleWarningClose} severity="warning" sx={{ width: '100%' }}>
            {warningMessage}
          </Alert>
        </Snackbar>

        {/* Full Screen Image Modal */}
        <Modal
          open={!!fullScreenImage}
          onClose={() => setFullScreenImage(null)}
          aria-labelledby="full-screen-image-title"
          aria-describedby="full-screen-image-description"
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <Fade in={!!fullScreenImage}>
            <Box sx={{ 
              position: 'relative', 
              maxWidth: '90vw', 
              maxHeight: '90vh', 
              bgcolor: 'background.paper', 
              boxShadow: 24, 
              p: '0.625rem', // 1 * 10px = 10px to rem (MUI spacing)
              outline: 'none' // Remove focus outline
            }}>
              <IconButton 
                onClick={() => setFullScreenImage(null)}
                sx={{ 
                  position: 'absolute', 
                  top: '0.5rem', // 8px to rem
                  right: '0.5rem', // 8px to rem
                  zIndex: 1, 
                  color: 'white', // Make close button visible on dark images
                  backgroundColor: 'rgba(0, 0, 0, 0.5)', // Background for visibility
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  }
                }}
                size="small"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
              <img 
                src={fullScreenImage || ''} 
                alt="Full screen generated image" 
                style={{ 
                  display: 'block', // Prevent extra space
                  maxWidth: '100%', 
                  maxHeight: 'calc(90vh - 1rem)', // 16px to rem
                  objectFit: 'contain' // Show the whole image without cropping
                }} 
              />
            </Box>
          </Fade>
        </Modal>

        {/* Credit Alert Dialog */}
        <CreditAlert
          open={creditAlertOpen}
          onClose={handleCreditAlertClose} // Use the new handler
          requiredCredits={requiredCredits}
          availableCredits={creditsLeft}
        />
      </Box> {/* <<< Added missing closing tag for the main Box started on line 1020 */}
    </> // Closing tag for the fragment added earlier
  );
}); // Close React.memo wrapper

const ChatTab = () => {
  const navigate = useNavigate();
  const { mode } = useThemeMode(); // Get current theme mode from context
  const currentTheme = mode === 'dark' ? darkTheme : lightTheme;
  const isMobile = useMediaQuery(currentTheme.breakpoints.down('sm')); // Detect mobile screen size
  const [session, setSession] = useState(null);

  // Helper function to format agent capabilities
  const formatAgentCapabilities = (instruction, minimal = false) => {
    // Remove the initial "Sen bir..." part if present
    const cleanInstruction = instruction.replace(/^Sen bir .*?\.\s*/i, '');
    
    if (minimal) {
      // For minimal display, extract just 3-4 key capabilities
      const capabilities = cleanInstruction
        .split('\n')
        .filter(line => line.trim())
        .map(line => line.replace(/^\s*-\s*/, '').trim())
        .filter(line => line.length > 0 && !line.includes('Sen ') && !line.includes('Kullanıcılara'));
      
      // Take just the first 3 capabilities for minimal display
      const keyCapabilities = capabilities.slice(0, 3);
      
      // Format them as a simple comma-separated list
      return keyCapabilities.join(' • ');
    } else {
      // Original formatting for detailed display
      return cleanInstruction
        .split('\n')
        .filter(line => line.trim())
        .map(line => line.replace(/^\s*-\s*/, '• '))
        .join('\n');
    }
  };

  // Define currentAgent first
  const [currentAgent, setCurrentAgent] = useState({
    id: 'emlak-danismani', // Default agent
    name: 'Emlak Danışmanı',
    type: 'text',
    systemInstruction: `Sen bir emlak danışmanısın. Kullanıcılara emlak alım-satım, kiralama, yatırım tavsiyeleri ve piyasa analizleri konusunda profesyonel yardım sağlamalısın. 
    - Gayrimenkul değerleme yapabilirsin
    - Semt analizi ve karşılaştırması yapabilirsin
    - Yatırım getirisi hesaplayabilirsin
    - Kira sözleşmeleri hakkında bilgi verebilirsin
    - Vergi ve yasal konularda temel bilgileri paylaşabilirsin
    - Müşteriye özel teklifler oluşturabilirsin`,
    avatar: '🏠'
  });

  // Now define messages using currentAgent - initial state will be set in useEffect
  const [messages, setMessages] = useState([]);
  const {
    isLoading,
    progress,
    loadingMessage,
    generateImage, // Function for image generation
    generateText,  // Function for text generation
    translateTextWithGemini // Destructure the new function
  } = useGenerationService(session); // Renamed hook, pass session
  const imageDimensions = { width: 1024, height: 1024, upscaleBy: 1 };

  
  // State for panel visibility - start with panels hidden
  const [panelsVisible, setPanelsVisible] = useState(false);

  // Sidebar state
  const [activeSection, setActiveSection] = useState('chat');
  
  // Chat history state
  const [chatSessions, setChatSessions] = useState([]);
  const [currentChatSession, setCurrentChatSession] = useState(null);
  const [savingChat, setSavingChat] = useState(false);
  const [saveChatDialogOpen, setSaveChatDialogOpen] = useState(false);
  const [isChatDirty, setIsChatDirty] = useState(false); // Track unsaved changes
  const [uploadedFileForChat, setUploadedFileForChat] = useState(null); // State for uploaded file at ChatTab level
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false); // State for mobile drawer
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  // Function to generate the initial agent message with quick prompts
  const createInitialAgentMessage = (agent) => {
    return {
      text: `Merhaba, ben ${agent.name}.`,
      sender: 'system',
      timestamp: new Date(),
      isInitialMessage: true, // Flag to identify initial messages for showing quick prompts
      quickPrompts: generateQuickPromptsForAgent(agent) // Add quick prompts based on agent type
    };
  };

  // Generate relevant quick prompts based on agent type and name
  const generateQuickPromptsForAgent = (agent) => {
    // Default prompts if none match - more minimal
    let prompts = ["Nasıl yardımcı olabilirsin?", "Özelliklerini anlat"];
    
    // Image generation agent - more minimal
    if (agent.type === 'image') {
      return [
        "Modern oturma odası",
        "Deniz manzaralı villa",
        "Bahçeli ev",
        "Minimalist mutfak"
      ];
    }
    
    // Real estate specific prompts - more minimal
    if (agent.name === 'Emlak Danışmanı') {
      return [
        "Yatırımlık daire önerileri",
        "Ev alırken dikkat edilecekler",
        "İstanbul'daki konut fiyatları grafiği"
      ];
    }
    
    if (agent.name === 'Konut Kredisi Uzmanı') {
      return [
        "Güncel faiz oranları",
        "Gerekli belgeler",
        "Kredi ödeme planı grafiği oluştur"
      ];
    }
    
    if (agent.name === 'Tapu ve Kadastro Uzmanı') {
      return [
        "Tapu devri süreci",
        "İpotek işlemleri",
        "Tapu harcı hesaplama grafiği"
      ];
    }
    
    if (agent.name === 'Gayrimenkul Değerleme Uzmanı') {
      return [
        "Değeri etkileyen faktörler",
        "Değerleme raporu",
        "Son 5 yıl değer artış grafiği"
      ];
    }
    
    if (agent.name === 'Kentsel Dönüşüm Danışmanı') {
      return [
        "Dönüşüm süreci",
        "Riskli yapı tespiti",
        "Dönüşüm bölgeleri grafiği"
      ];
    }
    
    if (agent.name === 'Yatırım Danışmanı (Gayrimenkul)') {
      return [
        "Yatırım stratejileri",
        "Getiri oranı yüksek bölgeler",
        "Yatırım getiri grafiği oluştur"
      ];
    }
    
    if (agent.name === 'Emlak Hukuku Danışmanı') {
      return [
        "Kira sözleşmesi",
        "Tahliye süreci",
        "Dava süreçleri grafiği"
      ];
    }
    
    if (agent.name === 'Görsel Düzenleyici') {
      return [
        "Görseli aydınlat",
        "Modern mobilya ekle",
        "Arka planı değiştir"
      ];
    }
    
    return prompts; // Return default prompts if no specific match
  };

  // Update messages state and mark chat as dirty (memoized)
  const updateMessages = useCallback((newMessagesOrFn) => {
    setMessages(prevMessages => {
      const newMessages = typeof newMessagesOrFn === 'function' 
        ? newMessagesOrFn(prevMessages) 
        : newMessagesOrFn;
      
      // Mark dirty if session exists or has more than initial message
      if (currentChatSession || newMessages.length > 1) { 
        setIsChatDirty(true);
      }
      return newMessages;
    });
  }, [currentChatSession]); // Dependency: currentChatSession

  // Handle agent change: update agent state but keep messages
  const handleAgentChange = (newAgent) => {
    setCurrentAgent(newAgent);
    
    // Check if we're in a conversation or just starting
    if (messages.length <= 1) {
      // If no conversation yet, create a new initial message with quick prompts
      setMessages([createInitialAgentMessage(newAgent)]);
    } else {
      // If in a conversation, add a transition message without quick prompts
      const transitionMessage = {
        text: `Şimdi ${newAgent.name} olarak devam ediyorum. Size nasıl yardımcı olabilirim?`,
        sender: 'system',
        timestamp: new Date(),
        isAgentTransition: true // Flag to identify agent transition messages
      };
      updateMessages([...messages, transitionMessage]);
    }
    
    setCurrentChatSession(null); // Changing agent starts a new unsaved context
    setIsChatDirty(true); // Mark as dirty because the agent context changed
    setUploadedFileForChat(null); // Clear any persistent uploaded file when changing agents
  };

  useEffect(() => {
    // Set initial message based on the default agent when component mounts
    const initialMessage = createInitialAgentMessage(currentAgent);
    setMessages([initialMessage]);

    const getSession = async () => {
      const { data } = await supabase.auth.getSession();
      setSession(data.session);
    };
    getSession();
  }, []);
  
  // Handle search functionality
  const handleSearch = (query) => {
    setSearchQuery(query);
    
    if (!query.trim()) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }
    
    setIsSearching(true);
    
    // Search through ONLY current chat messages
    // This ensures search is limited to the chat area only
    const results = messages.filter(msg => 
      msg.text && msg.text.toLowerCase().includes(query.toLowerCase())
    );
    
    setSearchResults(results);
    setIsSearching(false);
  };

  // No auto-hide functionality based on inactivity
  
  // Add keyboard shortcut for search (⌘+K or Ctrl+K)
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Check for Command+K (Mac) or Control+K (Windows/Linux)
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        // Focus the search input
        document.querySelector('input[placeholder="Sohbette ara..."]')?.focus();
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // Fetch chat sessions when component mounts
  useEffect(() => {
    if (session?.user?.id) {
      const initChatTables = async () => {
        try {
          // Check if chat tables exist
          const tablesExist = await ensureChatTablesExist();
          
          if (tablesExist) {
            // Fetch chat sessions only if tables exist
            fetchChatSessionsWrapper(); // Call the renamed wrapper function
          } else {
            console.warn('Chat tables do not exist. Chat history functionality will be limited.');
          }
        } catch (error) {
          console.error('Error checking chat tables:', error);
        }
      };
      
      initChatTables();
    }
  }, [session]);
  
  const fetchChatSessionsWrapper = async () => { // Renamed to avoid conflict with imported function
    try {
      // Use the imported function directly
      const data = await fetchChatSessions(session.user.id);
      setChatSessions(data || []);
    } catch (error) {
      console.error('Error fetching chat sessions wrapper:', error);
      // Show a user-friendly error message
      alert('Sohbet geçmişi yüklenirken bir hata oluştu. Lütfen daha sonra tekrar deneyin.');
    }
  };
  
  // Function to handle saving a chat (memoized)
  const handleSaveChat = useCallback(() => {
    if (messages.length <= 1) return; // Don't save empty chats
    setSaveChatDialogOpen(true);
  }, [messages]); // Dependency: messages array length check
  
  // Function to save chat with title
  const saveChat = async (title) => {
    try {
      setSavingChat(true);
      
      // Get the last message from the user or system
      const lastMessage = [...messages]
        .reverse()
        .find(msg => msg.sender === 'user' || msg.sender === 'system')?.text || '';
      
      let sessionData = currentChatSession; // Start with existing session if available

      if (sessionData) {
        // --- Update Existing Session ---
        // 1. Update session metadata
        sessionData = await updateChatSession(sessionData.id, {
          title,
          last_message: lastMessage
        });

        // 2. Delete old messages for this session
        const { error: deleteError } = await supabase
          .from('chat_messages')
          .delete()
          .eq('session_id', sessionData.id);

        if (deleteError) {
          console.error("Error deleting old messages:", deleteError);
          throw new Error("Could not update messages. Please try saving again.");
        }

        // 3. Save the current messages
        const savedMessages = await saveChatMessages(sessionData.id, session.user.id, messages);

        // 4. Save files/images/charts for the messages (consider optimizing this later if needed)
        for (let i = 0; i < messages.length; i++) {
          const msg = messages[i];
          const savedMsg = savedMessages[i];
          
          // Save file if exists using imported function
          if (msg.file) {
            await saveFileForMessage(savedMsg.id, msg.file);
          }
          
          // Save images if exists using imported function
          if (msg.images && msg.images.length > 0) {
            await saveImagesForMessage(savedMsg.id, msg.images);
          }
          
          // Save chart data if exists
          if (msg.chartData) {
            const { error } = await supabase
              .from('chat_messages')
              .update({
                chart_data: msg.chartData
              })
              .eq('id', savedMsg.id);
              
            if (error) {
              console.error("Error saving chart data:", error);
            }
          }
        }

      } else {
        // --- Create New Session ---
        // 1. Create the session
        sessionData = await saveChatSession(session.user.id, title, lastMessage);

        // 2. Save the messages
        const savedMessages = await saveChatMessages(sessionData.id, session.user.id, messages);

        // 3. Save files/images/charts for the messages
        for (let i = 0; i < messages.length; i++) {
          const msg = messages[i];
          const savedMsg = savedMessages[i];

          // Save file if exists using imported function
          if (msg.file) {
            await saveFileForMessage(savedMsg.id, msg.file);
          }

          // Save images if exists using imported function
          if (msg.images && msg.images.length > 0) {
            await saveImagesForMessage(savedMsg.id, msg.images);
          }
          
          // Save chart data if exists
          if (msg.chartData) {
            const { error } = await supabase
              .from('chat_messages')
              .update({
                chart_data: msg.chartData
              })
              .eq('id', savedMsg.id);
              
            if (error) {
              console.error("Error saving chart data:", error);
            }
          }
        }
      }

      // --- Post-Save Actions ---
      setCurrentChatSession(sessionData); // Update the current session state
      setIsChatDirty(false); // Mark chat as clean after successful save
      fetchChatSessionsWrapper(); // Refresh the history list
      setSaveChatDialogOpen(false); // Close the dialog

    } catch (error) {
      console.error('Error saving chat:', error);
      // Show error message
    } finally {
      setSavingChat(false);
    }
  };
  
  // Function to load a chat session
  const loadChatSession = async (sessionId) => {
    try {
      // Use imported function
      const loadedMessages = await fetchChatMessages(sessionId);
      
      if (loadedMessages && loadedMessages.length > 0) {
        setMessages(loadedMessages);
        
        // Get the session details
        const { data, error } = await supabase
          .from('chat_sessions')
          .select('*')
          .eq('id', sessionId)
          .single();
        
        if (!error) {
          setCurrentChatSession(data);
        }
      }
       
      setIsChatDirty(false); // Mark as clean when loading a session
      setActiveSection('chat');
    } catch (error) {
      console.error('Error loading chat session:', error);
    }
  };
  
  // Function to start a new chat
  const startNewChat = () => {
    // Use the helper function with the currently selected agent
    setMessages([createInitialAgentMessage(currentAgent)]);
    setCurrentChatSession(null);
    setIsChatDirty(false); // Mark as clean when starting a new chat
    setActiveSection('chat');
  };

  // const handleModelMenuOpen = (event) => { ... }; // Removed
  // const handleModelMenuClose = () => { ... }; // Removed
  // const handleModelSelect = (model) => { ... }; // Removed

  return (
    <ThemeProvider theme={currentTheme}>
      <Box sx={{
        display: 'flex', 
        height: '100vh', 
        overflow: 'hidden',
        width: '100vw',
        maxWidth: '100%',
        position: 'fixed', // Fix the main container
        top: 0,
        left: 0
      }}>
        {/* Sidebar - Conditionally render based on screen size */}
        {!isMobile && (
          <Drawer
            variant="permanent"
            sx={{
            width: drawerWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
              borderRight: '1px solid',
              borderColor: 'divider',
              backgroundColor: 'background.paper',
            },
          }}
        >
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            height: '100%', 
            alignItems: 'center',
            py: 2
          }}>
            <Tooltip title="Yeni Sohbet" placement="right">
              <IconButton 
                sx={{ 
                  mb: 2,
                  backgroundColor: 'primary.main',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  }
                }}
                onClick={() => {
                  setActiveSection('chat');
                  // Correctly call startNewChat which handles setting the initial message
                  startNewChat(); 
                }}
              >
                <AddIcon />
              </IconButton>
            </Tooltip>
            
            <List sx={{ width: '100%' }}>
              <ListItem sx={{ justifyContent: 'center' }}>
                <Tooltip title="Sohbet" placement="right">
                  <SidebarButton 
                    active={activeSection === 'chat'} 
                    onClick={() => setActiveSection('chat')}
                  >
                    <ChatIcon />
                  </SidebarButton>
                </Tooltip>
              </ListItem>
              
              {/* History button */}
              <ListItem sx={{ justifyContent: 'center' }}>
                <Tooltip title="Sohbet Geçmişi" placement="right">
                  <SidebarButton 
                    active={activeSection === 'history'} 
                    onClick={() => setActiveSection('history')}
                  >
                    <HistoryIcon />
                  </SidebarButton>
                </Tooltip>
              </ListItem>
              
              <ListItem sx={{ justifyContent: 'center' }}>
                <Tooltip title="Mülklerim" placement="right">
                  <SidebarButton 
                    active={activeSection === 'properties'} 
                    onClick={() => setActiveSection('properties')}
                  >
                    <Badge color="primary" variant="dot">
                      <HomeIcon />
                    </Badge>
                  </SidebarButton>
                </Tooltip>
              </ListItem>
              <ListItem sx={{ justifyContent: 'center' }}>
                <Tooltip title="Profil" placement="right">
                  <SidebarButton 
                    active={activeSection === 'profile'} 
                    onClick={() => navigate('/dashboard')}
                  >
                    <PersonIcon />
                  </SidebarButton>
                </Tooltip>
              </ListItem>
              <ListItem sx={{ justifyContent: 'center' }}>
                <Tooltip title="Görsellerim" placement="right">
                  <SidebarButton 
                    active={activeSection === 'gallery'} 
                    onClick={() => setActiveSection('gallery')}
                  >
                    <FolderIcon />
                  </SidebarButton>
                </Tooltip>
              </ListItem>
              <ListItem sx={{ justifyContent: 'center' }}>
                <Tooltip title="Paylaşımlarım" placement="right">
                  <SidebarButton 
                    active={activeSection === 'shares'} 
                    onClick={() => navigate('/shared-links')}
                  >
                    <LinkIcon />
                  </SidebarButton>
                </Tooltip>
              </ListItem>
            </List>
            
            <Box sx={{ flexGrow: 1 }} />
            
            <List sx={{ width: '100%' }}>
              <ListItem sx={{ justifyContent: 'center' }}>
                <Tooltip title="Ayarlar" placement="right">
                  <SidebarButton 
                    active={activeSection === 'settings'} 
                    onClick={() => navigate('/subscription')}
                  >
                    <SettingsIcon />
                  </SidebarButton>
                </Tooltip>
              </ListItem>
            </List>
            {/* Removed extra closing List tag */}
          </Box>
        </Drawer>
        )}

        {/* Temporary Drawer for Mobile */}
        {isMobile && (
          <Drawer
            variant="temporary"
            open={mobileDrawerOpen}
            onClose={() => setMobileDrawerOpen(false)}
            ModalProps={{
              keepMounted: true, // Better open performance on mobile.
            }}
            sx={{
              '& .MuiDrawer-paper': {
                boxSizing: 'border-box',
                width: drawerWidth, // Use the same width as the permanent drawer
                backgroundColor: 'background.paper',
              },
            }}
          >
            {/* Content copied from the permanent drawer */}
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              alignItems: 'center',
              py: 2
            }}>
              <Tooltip title="Yeni Sohbet" placement="right">
                <IconButton
                  sx={{
                    mb: 2,
                    backgroundColor: 'primary.main',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'primary.dark',
                    }
                  }}
                  onClick={() => {
                    setActiveSection('chat');
                    startNewChat();
                    setMobileDrawerOpen(false); // Close drawer
                  }}
                >
                  <AddIcon />
                </IconButton>
              </Tooltip>

              <List sx={{ width: '100%' }}>
                <ListItem sx={{ justifyContent: 'center' }}>
                  <Tooltip title="Sohbet" placement="right">
                    <SidebarButton
                      active={activeSection === 'chat'}
                      onClick={() => { setActiveSection('chat'); setMobileDrawerOpen(false); }}
                    >
                      <ChatIcon />
                    </SidebarButton>
                  </Tooltip>
                </ListItem>

                <ListItem sx={{ justifyContent: 'center' }}>
                  <Tooltip title="Sohbet Geçmişi" placement="right">
                    <SidebarButton
                      active={activeSection === 'history'}
                      onClick={() => { setActiveSection('history'); setMobileDrawerOpen(false); }}
                    >
                      <HistoryIcon />
                    </SidebarButton>
                  </Tooltip>
                </ListItem>

                <ListItem sx={{ justifyContent: 'center' }}>
                  <Tooltip title="Mülklerim" placement="right">
                    <SidebarButton
                      active={activeSection === 'properties'}
                      onClick={() => { setActiveSection('properties'); setMobileDrawerOpen(false); }}
                    >
                      <Badge color="primary" variant="dot">
                        <HomeIcon />
                      </Badge>
                    </SidebarButton>
                  </Tooltip>
                </ListItem>
                <ListItem sx={{ justifyContent: 'center' }}>
                  <Tooltip title="Profil" placement="right">
                    <SidebarButton
                      active={activeSection === 'profile'}
                      onClick={() => { navigate('/dashboard'); setMobileDrawerOpen(false); }}
                    >
                      <PersonIcon />
                    </SidebarButton>
                  </Tooltip>
                </ListItem>
                <ListItem sx={{ justifyContent: 'center' }}>
                  <Tooltip title="Görsellerim" placement="right">
                    <SidebarButton
                      active={activeSection === 'gallery'}
                      onClick={() => { setActiveSection('gallery'); setMobileDrawerOpen(false); }}
                    >
                      <FolderIcon />
                    </SidebarButton>
                  </Tooltip>
                </ListItem>
                <ListItem sx={{ justifyContent: 'center' }}>
                  <Tooltip title="Paylaşımlarım" placement="right">
                    <SidebarButton
                      active={activeSection === 'shares'}
                      onClick={() => { navigate('/shared-links'); setMobileDrawerOpen(false); }}
                    >
                      <LinkIcon />
                    </SidebarButton>
                  </Tooltip>
                </ListItem>
              </List>

              <Box sx={{ flexGrow: 1 }} />

              <List sx={{ width: '100%' }}>
                <ListItem sx={{ justifyContent: 'center' }}>
                  <Tooltip title="Ayarlar" placement="right">
                    <SidebarButton
                      active={activeSection === 'settings'}
                      onClick={() => { navigate('/subscription'); setMobileDrawerOpen(false); }}
                    >
                      <SettingsIcon />
                    </SidebarButton>
                  </Tooltip>
                </ListItem>
              </List>
            </Box>
          </Drawer>
        )}

        {/* Main content */}
        <Box sx={{
          flexGrow: 1, 
          display: 'flex', 
          flexDirection: 'column',
          overflow: 'hidden', // Prevent scrolling at this level
          backgroundColor: 'background.default',
          width: '100vw', // Use viewport width
          maxWidth: '100%', // Prevent any max-width constraints
          minWidth: 0, // Prevent flex items from overflowing
          alignItems: 'stretch', // Make children stretch to fill width
          margin: 0,
          padding: 0,
          // Adjust width calculation if sidebar is hidden
          width: isMobile ? '100vw' : `calc(100vw - ${drawerWidth})`,
          height: '100vh' // Ensure it takes full viewport height
        }}>
          {/* Header */}
          <AppBar 
            position="sticky" 
            color="transparent" 
            elevation={0}
            sx={{ 
              borderBottom: '1px solid',
              borderColor: 'divider',
              top: 0,
              zIndex: 1100,
            }}
          >
            <Toolbar sx={{ justifyContent: 'space-between' }}>
              {/* Hamburger Menu Button for Mobile */}
              {isMobile && (
                <IconButton
                  color="inherit"
                  aria-label="open drawer"
                  edge="start"
                  onClick={() => setMobileDrawerOpen(true)}
                  sx={{ mr: 2 }}
                >
                  <MenuIcon />
                </IconButton>
              )}
              {/* Model Selection Button and Menu Removed */}
              {/* <Button ... /> */}
              {/* <Menu ... /> */}

              {/* Agent info and credits */}
              <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                {/* Left side: Agent info */}
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar 
                    sx={{ 
                      bgcolor: 'primary.main', 
                      mr: 2,
                      width: 40,
                      height: 40,
                      fontSize: '1.5rem'
                    }}
                  >
                    {currentAgent.avatar || '🏠'}
                  </Avatar>
                  <Box>
                    <Typography variant="h6" component="div" sx={{ fontWeight: 500 }}>
                      {currentAgent.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatAgentCapabilities(currentAgent.systemInstruction, true)}
                    </Typography>
                  </Box>
                </Box>
                
                {/* Right side: User credits/info removed */}
              </Box>
              
              {/* Search with functionality */}
              <Box sx={{ position: 'relative' }}>
                <TextField
                  placeholder="Sohbette ara..."
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      {isSearching ? (
                        <CircularProgress size={16} />
                      ) : searchQuery ? (
                        <IconButton size="small" onClick={() => handleSearch('')}>
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          ⌘ K
                        </Typography>
                      )}
                    </InputAdornment>
                  ),
                }}
                sx={{
                  width: '12.5rem', // 200px to rem
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '1rem', // 2 * 8px = 16px to rem
                    backgroundColor: 'background.paper',
                    '& fieldset': {
                      borderColor: 'divider',
                    },
                    '&:hover fieldset': {
                      borderColor: 'primary.main',
                    },
                  },
                }}
              />
              
              {/* Search Results Dropdown */}
              {searchQuery && searchResults.length > 0 && (
                <Paper
                  sx={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    mt: 1,
                    width: 300,
                    maxHeight: 400,
                    overflow: 'auto',
                    zIndex: 1200, // Further increased z-index to appear above all other elements
                    boxShadow: 3,
                    backgroundColor: 'background.paper', // Ensure background is visible
                  }}
                >
                  <List dense>
                    <ListItem>
                      <ListItemText 
                        primary={`${searchResults.length} sonuç bulundu`} 
                        primaryTypographyProps={{ fontWeight: 'bold' }}
                      />
                    </ListItem>
                    <Divider />
                    {searchResults.map((result, index) => (
                      <ListItem 
                        key={index} 
                        button
                        onClick={() => {
                          // Find the index of this message in the full messages array
                          const messageIndex = messages.findIndex(m => 
                            m.text === result.text && m.timestamp.getTime() === result.timestamp.getTime()
                          );
                          
                          if (messageIndex >= 0) {
                            // Set active section to chat if not already
                            if (activeSection !== 'chat') {
                              setActiveSection('chat');
                            }
                            
                            // Use setTimeout to ensure DOM is updated after section change
                            setTimeout(() => {
                              // Find the chat window element - more specific selector
                              const chatWindow = document.querySelector('[data-chat-window="true"]');
                          
                              if (chatWindow) {
                                // Find all message bubbles within the chat window only
                                const messageElements = chatWindow.querySelectorAll('.message-bubble');
                            
                                if (messageElements[messageIndex]) {
                                  // Calculate the position of the message relative to the chat window
                                  const messageTop = messageElements[messageIndex].offsetTop;
                              
                                  // Scroll only the chat window to the message position
                                  chatWindow.scrollTo({
                                    top: messageTop - 100, // Offset to show some context above
                                    behavior: 'smooth'
                                  });
                              
                                  // Highlight the message temporarily
                                  messageElements[messageIndex].style.backgroundColor = alpha(currentTheme.palette.primary.main, 0.2);
                                  setTimeout(() => {
                                    messageElements[messageIndex].style.backgroundColor = '';
                                  }, 2000);
                                }
                              }
                            }, 100);
                          }
                          setSearchQuery(''); // Clear search after navigating
                        }}
                      >
                        <ListItemText
                          primary={result.sender === 'user' ? 'Siz' : currentAgent.name}
                          secondary={result.text.length > 60 ? result.text.substring(0, 60) + '...' : result.text}
                          primaryTypographyProps={{ 
                            color: result.sender === 'user' ? 'primary' : 'textPrimary',
                            fontWeight: 'medium'
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              )}
            </Box>
            </Toolbar>
          </AppBar>
          
          {/* Render the appropriate section */}
          {activeSection === 'chat' && (
            <>
              {/* Toggle button for panels */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                mt: 1, 
                mb: panelsVisible ? 0 : 2,
                transition: 'all 0.3s ease',
                width: '100%',
                position: 'relative',
                zIndex: 1,
                maxWidth: '100vw',
                // Remove negative transform on mobile
                transform: isMobile ? 'none' : 'translateX(-90px)' 
              }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setPanelsVisible(!panelsVisible)}
                  startIcon={panelsVisible ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  sx={{ 
                    borderRadius: '1.25rem', // 20px to rem
                    px: 2,
                    boxShadow: theme => theme.shadows[1],
                    backgroundColor: 'background.paper',
                    '&:hover': {
                      backgroundColor: 'background.paper',
                    }
                  }}
                >
                  {panelsVisible ? 'Asistanları Gizle' : 'Asistanları Göster'}
                </Button>
              </Box>
              
              {/* Agent selection - conditionally rendered */}
              {panelsVisible && (
                <Box sx={{ 
                  p: 3, 
                  pb: 1,
                  transition: 'all 0.3s ease',
                  animation: 'fadeIn 0.3s ease-out forwards',
                }}> 
                  <AgentMemory
                    session={session}
                    currentAgent={currentAgent} // Pass current agent state
                    setCurrentAgent={handleAgentChange} // Pass the new handler function
                  />
                </Box>
              )}

              {/* Chat area */}
              <Box sx={{ 
                flexGrow: 1, 
                display: 'flex', 
                flexDirection: 'column',
                overflow: 'hidden', // Keep this to prevent outer scrolling
                px: { xs: 1, sm: 2, md: 3 }, // Responsive padding
                pb: 3,
                transition: 'all 0.3s ease',
                // Adjust height to ensure input area is visible
                height: panelsVisible ? 'calc(100vh - 26rem)' : 'calc(100vh - 20rem)', 
                width: '100%',
                maxWidth: '100%', // Ensure full width
                alignSelf: 'stretch', // Make sure it stretches to fill parent
                boxSizing: 'border-box' // Include padding in width calculation
              }}>
                <Paper 
                  elevation={0} 
                  sx={{ 
                    flexGrow: 1, 
                    display: 'flex', 
                    flexDirection: 'column',
                    overflow: 'hidden', // This prevents the Paper from scrolling
                    borderRadius: '1.5rem', // 3 * 8px = 24px to rem
                    border: '1px solid',
                    borderColor: 'divider',
                    width: '100%', // Ensure full width
                    maxWidth: '100%', // Prevent any max-width constraints
                    alignSelf: 'stretch', // Make sure it stretches to fill parent
                    minWidth: 0, // Prevent flex items from overflowing
                    boxSizing: 'border-box', // Include padding in width calculation
                    m: 0, // No margin
                    height: '100%' // Ensure it takes full height
                  }}
                >
                  <ChatComponent 
                    session={session}
                    messages={messages}
                    setMessages={setMessages} // Keep setMessages for now, though updateMessages is preferred
                    updateMessages={updateMessages} // Pass the wrapper function
                    isLoading={isLoading}
                    progress={progress}
                    loadingMessage={loadingMessage}
                    imageDimensions={imageDimensions}
                    onGenerateImage={generateImage} // Pass image generation function
                    onGenerateText={generateText}   // Pass text generation function
                    translateTextWithGemini={translateTextWithGemini} // Pass translation function
                    currentAgent={currentAgent}     // Pass selected agent
                    onSaveChat={handleSaveChat}
                    // isSaved is true only if session exists AND there are no unsaved changes
                    isSaved={currentChatSession !== null && !isChatDirty}
                    uploadedFile={uploadedFileForChat} // Pass state down
                    setUploadedFile={setUploadedFileForChat} // Pass setter down
                  />
                </Paper>
              </Box>
            </>
          )}
          
          {activeSection === 'history' && (
            <Box sx={{ 
              flexGrow: 1, 
              display: 'flex', 
              flexDirection: 'column',
              overflow: 'hidden',
              p: 3
            }}>
              <ChatHistory 
                onSelectChat={loadChatSession}
                onNewChat={startNewChat}
                session={session}
              />
            </Box>
          )}

          {activeSection === 'gallery' && (
            <Box sx={{ 
              flexGrow: 1, 
              display: 'flex', 
              flexDirection: 'column',
              overflow: 'hidden',
              p: 3
            }}>
              <Gallery />
            </Box>
          )}

          {activeSection === 'properties' && (
            <Box sx={{ 
              flexGrow: 1, 
              display: 'flex', 
              flexDirection: 'column',
              overflow: 'hidden',
              p: 3
            }}>
              <PropertyGallery />
            </Box>
          )}
        </Box>
      </Box>
      
      {/* Save Chat Dialog */}
      <SaveChatDialog
        open={saveChatDialogOpen}
        onClose={() => setSaveChatDialogOpen(false)}
        onSave={saveChat}
        defaultTitle={currentChatSession?.title || ''}
        saving={savingChat}
      />
    </ThemeProvider>
  );
};

export default ChatTab;
